import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { isGrowthProduction } from "../utils/growthProductionGuard";
import {
  Box,
  Grid,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  Stack,
  Avatar,
  Tabs,
  Tab,
  Button,
  Drawer,
  IconButton,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Badge,
  InputAdornment,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import API from "../api/api";
import EmailOtpModal from "../components/common/EmailOtpModal";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SavingsIcon from "@mui/icons-material/Savings";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import PremiumScreenHeader from "../components/common/PremiumScreenHeader";
import BottomNav from "../components/common/BottomNav";

/** ---------- helpers ---------- */
function fmtAmount(value) {
  const num = Number(value || 0);
  return num.toFixed(2);
}

function maskUsernameMid(u) {
  const s = String(u || "").trim();
  const digitsOnly = s.replace(/\D/g, "");
  if (digitsOnly.length === 10) {
    // Exactly hide middle 4 numbers: e.g. 9999999999 -> 999****999, 8095918105 -> 809****105
    return `${s.slice(0, 3)}****${s.slice(-3)}`;
  }
  if (digitsOnly.length >= 8) {
    const len = s.length;
    const hideLen = 4;
    const pre = Math.floor((len - hideLen) / 2);
    return `${s.slice(0, pre)}****${s.slice(pre + hideLen)}`;
  }
  return s;
}

function counterpartyLabel(tx = {}) {
  const meta = tx?.meta || {};
  const st = String(tx?.source_type || "").toUpperCase();
  if (st === "ADMIN_MANUAL" || st === "ADMIN" || Boolean(meta?.by_admin)) {
    return "";
  }
  // Prefer metadata from wallet credit calls
  const from = meta.from_user || meta.tr_username || meta.username;
  const fromId = meta.from_user_id || meta.user_id;
  const u = from ? maskUsernameMid(from) : "";
  if (u) return `From ${u}`;
  if (fromId) return `From ID ${fromId}`;
  return "";
}

function humanizeType(t) {
  const map = {
    WITHDRAWABLE_CREDIT: "Income Credited",
    COMMISSION_CREDIT: "Commission Credit",
    DIRECT_REF_BONUS: "Direct Referral Bonus",
    AUTOPOOL_BONUS_FIVE: "Autopool Bonus",
    AUTOPOOL_BONUS_THREE: "Autopool Bonus",
    INCOME_CREDIT_75: "Main Wallet Income",
    SELF_ACCOUNT_CREDIT: "Self Account Credit",
    SELF_ACCOUNT_DEBIT: "Self Account Allocation (₹250)",
    TAX_POOL_CREDIT: "Tax / Admin Pool Credit",
    AUTO_ECOUPON_ISSUED: "E-Coupon Issued",
    AUTO_PURCHASE_DEBIT: "E-Coupon Issued",
  };

  const key = String(t || "").toUpperCase();
  if (map[key]) return map[key];

  try {
    const s = String(t || "TX").toLowerCase().replace(/_/g, " ");
    return s.replace(/\b\w/g, (c) => c.toUpperCase());
  } catch {
    return String(t || "TX");
  }
}

// Derive prime tier and friendly source message for transactions
function extractPrimeTier(meta = {}, tx = {}) {
  const src = String(meta.source || "").toUpperCase();
  const trig = String(meta.trigger || "").toUpperCase();
  const pool = String(meta.pool_type || "").toUpperCase();
  const st = String(tx?.source_type || "").toUpperCase();

  if (src.includes("_150") || trig.includes("150") || pool.includes("150")) return 150;
  if (src.includes("_750") || trig.includes("750")) return 750;
  if (src.includes("_759") || st === "MONTHLY_759" || src === "MONTHLY_759") return 759;

  const gross = Number(meta.gross);
  if (gross === 150) return 150;
  if (gross === 750) return 750;

  return undefined;
}

function describeSource(tx = {}) {
  const type = String(tx?.type || "").toUpperCase();
  const meta = tx?.meta || {};
  const src = String(meta.source || "").toUpperCase();
  const st = String(tx?.source_type || "").toUpperCase();
  const ot = String(meta.orig_type || "").toUpperCase();
  const tier = extractPrimeTier(meta, tx);

  // 0. Manual Wallet & Package Adjustments from Admin (all pockets debit or credit)
  const isByAdmin = st === "ADMIN_MANUAL" || st === "ADMIN" || Boolean(meta?.by_admin);
  const isAdjustment = type === "ADJUSTMENT_DEBIT" || type === "ADJUSTMENT_CREDIT" || type.startsWith("ADJUSTMENT_");
  if (isByAdmin || isAdjustment) {
    return "System Auto Adjustment";
  }

  // Keep existing label for debits
  if (type === "SELF_ACCOUNT_DEBIT") return humanizeType(type);

  // Reward points
  if (type === "RP_EARN") {
    if (st === "MONTHLY_759" || src === "MONTHLY_759" || tier === 759) {
      return "SSV 1000 - Reward Points";
    }
    return "Reward Points Earned";
  }

  // Self Rebirth 250 source
  if (st === "SELF_REBIRTH_250" || src.includes("REBIRTH_250") || meta.auto_rule === "SELF_REBIRTH_250") {
    if (type === "TAX_POOL_CREDIT") return "Self Rebirth - Franchise / Tax Share";
    if (ot === "DIRECT_REF_BONUS" || type === "DIRECT_REF_BONUS") return "Self Rebirth - Direct Sponsor Bonus";
    if (src.startsWith("FIVE_MATRIX") || ot === "AUTOPOOL_BONUS_FIVE") return "5 Blocks";
    if (src.startsWith("THREE_MATRIX") || ot === "AUTOPOOL_BONUS_THREE") return "3 Blocks";
  }

  // Rank upgrade commissions (override labels to e-Edu)
  const isRankUpgrade = st === "RANK_UPGRADE" || String(meta.kind || "").toUpperCase().startsWith("RANK_UPGRADE_");
  if (isRankUpgrade) {
    const orig = String(meta.orig_type || type || "").toUpperCase();
    if (orig === "DIRECT_REF_BONUS" || type === "DIRECT_REF_BONUS") {
      return "e-Edu Referral Bonus";
    }
    if (orig === "LEVEL_BONUS" || type === "LEVEL_BONUS") {
      const lvl = Number(meta.level ?? meta.level_index);
      return Number.isFinite(lvl) && lvl > 0 ? `e-Edu Layer ${lvl} Bonus` : "e-Edu Layer Bonus";
    }
  }

  // Daily Royalty Payouts
  if (type === "GLOBAL_ROYALTY" || ot === "GLOBAL_ROYALTY" || st === "DAILY_POOL_DISTRIBUTION") {
    if (meta?.tier) return `Daily Royalty Tier ${meta.tier} Payout`;
    return meta?.description || "Daily Royalty Payout";
  }

  // Referral bonuses
  if (
    type === "DIRECT_REF_BONUS" ||
    type === "PRIME_750_DIRECT" ||
    ot === "PRIME_750_DIRECT" ||
    type === "PRIME_150_DIRECT" ||
    ot === "PRIME_150_DIRECT" ||
    src === "JOIN_REFERRAL" ||
    st === "JOIN_REFERRAL" ||
    st === "PRIME_750" && (type === "DIRECT_REF_BONUS" || ot === "DIRECT_REF_BONUS" || type === "PRIME_750_DIRECT" || ot === "PRIME_750_DIRECT")
  ) {
    if (type === "PRIME_750_DIRECT" || ot === "PRIME_750_DIRECT" || tier === 750) return "Agent Subscription Referral Bonus";
    if (type === "PRIME_150_DIRECT" || ot === "PRIME_150_DIRECT" || tier === 150) return "Prime 150 Referral Bonus";
    if (tier) return `Referral Bonus ${tier} Prime`;
    return "Direct Referral Bonus";
  }

  // Prime self activations
  if (ot === "PRIME_150_SELF" || src === "PRIME_150_SELF" || type === "PRIME_150_SELF") return "Prime 150 Self Activation";
  if (ot === "PRIME_750_SELF" || src === "PRIME_750_SELF" || type === "PRIME_750_SELF") return "Join Subscription (₹1,000)";
  if (ot === "PRIME_759_SELF" || src === "PRIME_759_SELF" || type === "PRIME_759_SELF") return "Prime 1000 Self Activation";

  // Blocks autopool bonuses
  if (src.startsWith("THREE_MATRIX") || ot === "AUTOPOOL_BONUS_THREE") {
    return "3 Blocks";
  }
  if (src.startsWith("FIVE_MATRIX") || ot === "AUTOPOOL_BONUS_FIVE") {
    return "5 Blocks";
  }

  // Monthly 759 / SSV 1000 flows (Smart Subscription Voucher)
  const isSpp = st === "MONTHLY_759" || src === "MONTHLY_759" || src.includes("759") || st.startsWith("MONTHLY_FIRST_SEASON") || ot.includes("MONTHLY_759");
  if (isSpp) {
    if (ot === "MONTHLY_759_DIRECT" || type === "MONTHLY_759_DIRECT") {
      return "SSV Direct Referral Bonus";
    }
    if (ot === "MONTHLY_759_LEVEL" || type === "MONTHLY_759_LEVEL") {
      const lvl = Number(meta.level_index ?? meta.level);
      return Number.isFinite(lvl) && lvl > 0 ? `SSV Layer ${lvl} Bonus` : "SSV Layer Bonus";
    }
    if (ot === "AUTOPOOL_BONUS_FIVE" || src.startsWith("FIVE_MATRIX")) {
      const lvl = Number(meta.level_index ?? meta.level);
      return Number.isFinite(lvl) && lvl > 0 ? `5 Blocks Matrix - Layer ${lvl}` : "5 Blocks Matrix";
    }
    if (ot === "AUTOPOOL_BONUS_THREE" || src.startsWith("THREE_MATRIX")) {
      const lvl = Number(meta.level_index ?? meta.level);
      return Number.isFinite(lvl) && lvl > 0 ? `3 Blocks Matrix - Layer ${lvl}` : "3 Blocks Matrix";
    }
    if (ot === "MONTHLY_759_SELF") {
      return "SSV Personal Cashback";
    }
    return "SSV Referral Commission";
  }

  // Main Wallet Specific Transactions
  if (type === "SELF_PACKAGE_COUPON_CREATE" || meta.transfer_mode === "SELF") {
    const code = meta.voucher_code ? ` [${meta.voucher_code}]` : "";
    return `Self Package Coupon Created (E-Edu)${code}`;
  }
  if (type === "P2P_PACKAGE_COUPON_SEND" || type.includes("P2P_SEND") || type === "P2P_TRANSFER_SEND") {
    const rawRec = meta.recipient_phone || meta.recipient_username || meta.recipient_id || meta.to_user || "";
    const rec = rawRec ? maskUsernameMid(rawRec) : "Member";
    const fee = meta.fee_amount ? ` (7% Fee: -₹${Number(meta.fee_amount).toFixed(2)})` : "";
    return `Gift Voucher Sent to ${rec}${fee}`;
  }
  if (type === "P2P_PACKAGE_COUPON_RECEIVE" || type.includes("P2P_RECEIVE") || type === "P2P_TRANSFER_RECEIVE") {
    const rawSender = meta.sender_phone || meta.sender_username || meta.sender_id || meta.from_user || "";
    const sender = rawSender ? maskUsernameMid(rawSender) : "Member";
    return `Gift Voucher Received from ${sender}`;
  }
  if (type.includes("P2P")) {
    return "Gift Voucher";
  }
  if (type === "VOUCHER_REDEEM_CREDIT") {
    const code = meta.voucher_code ? ` [${meta.voucher_code}]` : "";
    const from = meta.creator_username ? ` from ${maskUsernameMid(meta.creator_username)}` : "";
    return `Coupon Redeemed to Main Wallet${code}${from}`;
  }
  if (type === "MAIN_TO_COUPON" || type === "COUPON_WALLET_TRANSFER_OUT") return "Transfer to Coupon Wallet";
  if (type === "VOUCHER_CREATE_DEBIT") return "Package Voucher Created";
  if (type === "WITHDRAWAL_DEBIT") return "Withdrawal Payout";
  if (type === "WITHDRAWAL_REFUND") return "Withdrawal Refund";
  if (type === "ADJUSTMENT_DEBIT") {
    return "System Auto Adjustment";
  }
  if (type === "ADJUSTMENT_CREDIT") return "System Balance Adjustment";
  if (type === "TAX_POOL_CREDIT") {
    if (st === "ADMIN_SERVICE_CHARGE" || src.includes("SERVICE_CHARGE")) return "System Auto Adjustment";
    return "Tax / Admin Pool Credit";
  }
  if (type === "INCOME_CREDIT_75" || type === "SELF_ACCOUNT_CREDIT") {
    if (ot) {
      const parentLabel = describeSource({ ...tx, type: ot });
      return `${parentLabel}`;
    }
    return type === "SELF_ACCOUNT_CREDIT" ? "Self Account Credit" : "Income Credited";
  }

  // Prime direct/self
  if (src === "PRIME_150" || st === "PRIME_150" || tier === 150) return "Prime 150";
  if (src === "PRIME_750" || st === "PRIME_750" || tier === 750) return "Join Subscription (₹1,000)";

  // Fallback
  return humanizeType(type) || "Wallet Transfer";
}

function isSsvLayerTx(tx) {
  const meta = tx?.meta || {};
  const ot = String(meta.orig_type || "").toUpperCase();
  const type = String(tx?.type || "").toUpperCase();
  const src = String(meta.source || "").toUpperCase();
  const st = String(tx?.source_type || "").toUpperCase();
  const trig = String(meta.trigger || "").toUpperCase();

  return (
    ot === "MONTHLY_759_LEVEL" ||
    type === "MONTHLY_759_LEVEL" ||
    ((st.includes("MONTHLY") || src === "MONTHLY_759" || trig === "MONTHLY_759") &&
      (meta.level_index !== undefined || meta.level !== undefined || ot.includes("LEVEL")))
  );
}

function isSsvBonusTx(tx) {
  const meta = tx?.meta || {};
  const ot = String(meta.orig_type || "").toUpperCase();
  const type = String(tx?.type || "").toUpperCase();
  const src = String(meta.source || "").toUpperCase();
  const st = String(tx?.source_type || "").toUpperCase();
  const trig = String(meta.trigger || "").toUpperCase();

  return (
    ot === "MONTHLY_759_DIRECT" ||
    type === "MONTHLY_759_DIRECT" ||
    ((st.includes("MONTHLY") || src === "MONTHLY_759" || trig === "MONTHLY_759") &&
      (ot.includes("DIRECT") || type.includes("DIRECT")))
  );
}

function isFiveLayerTx(tx) {
  const meta = tx?.meta || {};
  const ot = String(meta.orig_type || "").toUpperCase();
  const type = String(tx?.type || "").toUpperCase();
  const src = String(meta.source || "").toUpperCase();
  const plan = String(meta.plan || meta.matrix_type || "").toUpperCase();
  const kind = String(meta.kind || "").toUpperCase();
  const trig = String(meta.trigger || "").toUpperCase();

  return (
    type === "AUTOPOOL_BONUS_FIVE" ||
    ot === "AUTOPOOL_BONUS_FIVE" ||
    plan === "FIVE_150" ||
    src.includes("FIVE_MATRIX") ||
    kind.includes("FIVE_MATRIX") ||
    trig === "FIVE_150" ||
    (type === "INCOME_CREDIT_75" && (
      ot === "AUTOPOOL_BONUS_FIVE" ||
      src.includes("FIVE_MATRIX") ||
      plan === "FIVE_150" ||
      kind.includes("FIVE_MATRIX")
    ))
  );
}

function isThreeLayerTx(tx) {
  const meta = tx?.meta || {};
  const ot = String(meta.orig_type || "").toUpperCase();
  const type = String(tx?.type || "").toUpperCase();
  const src = String(meta.source || "").toUpperCase();
  const plan = String(meta.plan || meta.matrix_type || "").toUpperCase();
  const kind = String(meta.kind || "").toUpperCase();
  const trig = String(meta.trigger || "").toUpperCase();

  return (
    type === "AUTOPOOL_BONUS_THREE" ||
    ot === "AUTOPOOL_BONUS_THREE" ||
    plan === "THREE_150" ||
    src.includes("THREE_MATRIX") ||
    kind.includes("THREE_MATRIX") ||
    trig === "THREE_150" ||
    (type === "INCOME_CREDIT_75" && (
      ot === "AUTOPOOL_BONUS_THREE" ||
      src.includes("THREE_MATRIX") ||
      plan === "THREE_150" ||
      kind.includes("THREE_MATRIX")
    ))
  );
}

function isEEduLayerTx(tx) {
  const meta = tx?.meta || {};
  const ot = String(meta.orig_type || "").toUpperCase();
  const type = String(tx?.type || "").toUpperCase();
  const src = String(meta.source || "").toUpperCase();
  const st = String(tx?.source_type || "").toUpperCase();
  const kind = String(meta.kind || "").toUpperCase();

  const isEEdu = (
    st === "RANK_UPGRADE" ||
    st.includes("E_EDU") ||
    src.includes("E_EDU") ||
    meta.source === "E_EDU" ||
    kind.startsWith("RANK_UPGRADE") ||
    ot.startsWith("RANK_UPGRADE") ||
    type.includes("RANK_UPGRADE")
  );

  if (!isEEdu) return false;

  return (
    kind === "RANK_UPGRADE_LEVEL" ||
    ot === "LEVEL_BONUS" ||
    type === "LEVEL_BONUS" ||
    (meta.level !== undefined && Number(meta.level) > 0)
  );
}

function isEEduDirectTx(tx) {
  const meta = tx?.meta || {};
  const ot = String(meta.orig_type || "").toUpperCase();
  const type = String(tx?.type || "").toUpperCase();
  const src = String(meta.source || "").toUpperCase();
  const st = String(tx?.source_type || "").toUpperCase();
  const kind = String(meta.kind || "").toUpperCase();

  const isEEdu = (
    st === "RANK_UPGRADE" ||
    st.includes("E_EDU") ||
    src.includes("E_EDU") ||
    meta.source === "E_EDU" ||
    kind.startsWith("RANK_UPGRADE") ||
    ot.startsWith("RANK_UPGRADE") ||
    type.includes("RANK_UPGRADE")
  );

  if (!isEEdu) return false;

  return (
    kind === "RANK_UPGRADE_DIRECT" ||
    ot === "DIRECT_REF_BONUS" ||
    type === "DIRECT_REF_BONUS" ||
    meta.level === 0
  );
}

function ymd(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatHeaderDate(d) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(d);
  } catch {
    return d.toDateString();
  }
}

function groupByDay(items) {
  const now = new Date();
  const todayKey = ymd(now);
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  const yestKey = ymd(yest);

  const map = new Map();
  const order = [];

  (items || []).forEach((it) => {
    const k = it?.created_at ? ymd(it.created_at) : "unknown";
    if (!map.has(k)) {
      map.set(k, []);
      order.push(k);
    }
    map.get(k).push(it);
  });

  order.sort((a, b) => {
    if (a === "unknown" && b === "unknown") return 0;
    if (a === "unknown") return 1;
    if (b === "unknown") return -1;
    return a > b ? -1 : a < b ? 1 : 0;
  });

  return order.map((k) => {
    let title = "";
    if (k === todayKey) title = "Today";
    else if (k === yestKey) title = "Yesterday";
    else if (k === "unknown") title = "Unknown";
    else {
      const [Y, M, D] = k.split("-").map((x) => parseInt(x, 10));
      title = formatHeaderDate(new Date(Y, (M || 1) - 1, D || 1));
    }

    const rows = (map.get(k) || []).slice().sort((a, b) => {
      const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });

    return { title, rows };
  });
}

function groupByMonth(items) {
  const map = new Map();
  const order = [];

  (items || []).forEach((it) => {
    let key = "unknown";
    let monthLabel = "Recent Activity";
    if (it?.created_at) {
      const d = new Date(it.created_at);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        key = `${year}-${month}`;
        monthLabel = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
      }
    }
    if (!map.has(key)) {
      map.set(key, { label: monthLabel, rows: [], total: 0 });
      order.push(key);
    }
    const entry = map.get(key);
    entry.rows.push(it);
    const amt = Number(it?.amount || 0);
    if (amt > 0) {
      entry.total += amt;
    }
  });

  order.sort((a, b) => {
    if (a === "unknown" && b === "unknown") return 0;
    if (a === "unknown") return 1;
    if (b === "unknown") return -1;
    return a > b ? -1 : a < b ? 1 : 0;
  });

  return order.map((k) => {
    const entry = map.get(k);
    const sortedRows = (entry.rows || []).slice().sort((a, b) => {
      const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });
    return {
      key: k,
      title: entry.label,
      total: entry.total,
      rows: sortedRows,
    };
  });
}

/** ---------- UI atoms ---------- */
function StatusChip({ tx }) {
  const pending =
    (tx?.meta && (tx.meta.pending_due_to_inactive === true || tx.meta.pending === true)) ||
    String(tx?.status || "").toLowerCase() === "pending";

  return (
    <Chip
      size="small"
      label={pending ? "Pending" : "Success"}
      variant={pending ? "outlined" : "filled"}
      sx={{
        height: 20,
        fontSize: 11,
        fontWeight: 800,
        borderRadius: 999,
        px: 0.5,
        bgcolor: pending ? "transparent" : "success.light",
        color: pending ? "warning.main" : "success.dark",
        borderColor: pending ? "warning.main" : "transparent",
      }}
    />
  );
}

function AmountBadge({ value }) {
  const num = Number(value || 0);
  const isCredit = num >= 0;
  return (
    <Typography
      sx={{
        fontWeight: 900,
        fontSize: 14,
        color: isCredit ? "success.main" : "error.main",
        lineHeight: 1.1,
        letterSpacing: 0.2,
      }}
    >
      {isCredit ? "+" : "-"}₹ {fmtAmount(Math.abs(num))}
    </Typography>
  );
}

function RowIcon({ value, tx }) {
  const num = Number(value || 0);
  const isCredit = num >= 0;
  const cat = tx ? classifyTransaction(tx) : null;

  let icon = isCredit ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
  let bg = isCredit ? "#ECFDF5" : "#FEF2F2";
  let color = isCredit ? "#059669" : "#DC2626";
  let border = isCredit ? "1.5px solid #A7F3D0" : "1.5px solid #FECACA";

  if (cat === "E_EDU") {
    icon = <SchoolRoundedIcon fontSize="small" />;
    bg = "#EFF6FF";
    color = "#2563EB";
    border = "1.5px solid #BFDBFE";
  } else if (cat === "SAVE" || cat === "SELF_ACCOUNT") {
    icon = <SavingsIcon fontSize="small" />;
    bg = "#FEF3C7";
    color = "#D97706";
    border = "1.5px solid #FDE68A";
  } else if (cat === "QR_SCANNER") {
    icon = <QrCode2RoundedIcon fontSize="small" />;
    bg = "#ECFDF5";
    color = "#059669";
    border = "1.5px solid #A7F3D0";
  } else if (cat === "TRIZONE") {
    icon = <ShoppingCartRoundedIcon fontSize="small" />;
    bg = "#F0F9FF";
    color = "#0284C7";
    border = "1.5px solid #BAE6FD";
  } else if (cat === "ROYALTY") {
    icon = <WorkspacePremiumRoundedIcon fontSize="small" />;
    bg = "#FAF5FF";
    color = "#9333EA";
    border = "1.5px solid #E9D5FF";
  }

  return (
    <Avatar
      sx={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        bgcolor: bg,
        color: color,
        border: border,
      }}
      aria-label={isCredit ? "Credit" : "Debit"}
    >
      {icon}
    </Avatar>
  );
}

function MiniCard({ title, value, icon, color = "primary", onClick, selected }) {
  const colorMap = {
    primary: { border: "#BFDBFE", iconBg: "#EFF6FF", iconColor: "#1D4ED8" },
    success: { border: "#BBF7D0", iconBg: "#ECFDF5", iconColor: "#059669" },
    warning: { border: "#FDE68A", iconBg: "#FFFBEB", iconColor: "#D97706" },
  };
  const themeColors = colorMap[color] || colorMap.primary;

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        minWidth: 160,
        flexShrink: 0,
        p: 1.4,
        borderRadius: 2.5,
        borderColor: selected ? `${color}.main` : "#EEF2F6",
        borderWidth: selected ? 2 : 1,
        borderStyle: "solid",
        cursor: onClick ? "pointer" : "default",
        boxShadow: selected ? "0 6px 18px rgba(12, 45, 72, 0.12)" : "0 1px 4px rgba(15, 23, 42, 0.03)",
        display: "flex",
        alignItems: "center",
        gap: 1.2,
        bgcolor: "#FFFFFF",
        scrollSnapAlign: "start",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": onClick ? {
          borderColor: `${color}.main`,
          boxShadow: "0 6px 16px rgba(12, 45, 72, 0.08)",
          transform: "translateY(-1px)",
        } : {},
        "&:active": onClick ? {
          transform: "scale(0.99)",
        } : {},
      }}
    >
      <Avatar
        sx={{
          bgcolor: themeColors.iconBg,
          color: themeColors.iconColor,
          width: 38,
          height: 38,
          borderRadius: 2,
        }}
      >
        {icon}
      </Avatar>
      <Box>
        <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 800, fontSize: 11 }}>
          {title}
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 900, mt: 0.15, fontSize: 15, color: "#0F172A" }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}

function SectionHeader({ title, total }) {
  return (
    <Box sx={{ mt: 2, mb: 1.2, display: "flex", justifyContent: "space-between", alignItems: "center", px: 0.5 }}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 800,
          color: "#0F172A",
          letterSpacing: "0.2px",
        }}
      >
        {title}
      </Typography>
      {Number(total) > 0 && (
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#64748B" }}>
          ₹ {fmtAmount(total)} ▾
        </Typography>
      )}
    </Box>
  );
}

const CAT_CHIP_LABELS = {
  ADJUSTMENT: "System Adjustment",
  SELF_ACCOUNT: "Self blocks",
  E_EDU: "E-Edu",
  LAYER: "Layer & Blocks",
  DIRECT: "Direct Bonus",
  P2P: "Gift Voucher",
  ROYALTY: "Royalty Bonus",
  MERCHANT_CAPTAIN: "Merchant & Captain",
  WITHDRAWAL: "Withdrawal",
  REDEEM: "Redeemed / Pocket",
  OTHER: "Wallet Flow",
};

function classifyTransaction(tx) {
  const type = String(tx?.type || "").toUpperCase();
  const meta = tx?.meta || {};
  const src = String(meta.source || "").toUpperCase();
  const st = String(tx?.source_type || "").toUpperCase();
  const ot = String(meta.orig_type || "").toUpperCase();
  const trig = String(meta.trigger || "").toUpperCase();
  const kind = String(meta.kind || "").toUpperCase();

  // 0. SYSTEM AUTO ADJUSTMENT (From admin wallet adjustments)
  const isByAdmin = st === "ADMIN_MANUAL" || st === "ADMIN" || Boolean(meta?.by_admin);
  const isAdjustment = type === "ADJUSTMENT_DEBIT" || type === "ADJUSTMENT_CREDIT" || type.startsWith("ADJUSTMENT_");
  if (isByAdmin || isAdjustment) {
    return "ADJUSTMENT";
  }

  // 1. SELF ACCOUNT / REPURCHASE POCKET
  if (
    type.startsWith("SELF_ACCOUNT") ||
    meta.ledger === "SELF_ACCOUNT" ||
    type === "SELF_ACCOUNT_CREDIT" ||
    type === "SELF_ACCOUNT_DEBIT"
  ) {
    return "SELF_ACCOUNT";
  }

  // 2. E-EDU / RANK UPGRADE (all E-Edu layer commissions and referral bonuses)
  if (
    st === "RANK_UPGRADE" ||
    st.includes("E_EDU") ||
    src.includes("E_EDU") ||
    meta.source === "E_EDU" ||
    kind.startsWith("RANK_UPGRADE") ||
    ot.startsWith("RANK_UPGRADE") ||
    type.includes("RANK_UPGRADE")
  ) {
    return "E_EDU";
  }

  // 3. LAYER & AUTOPOOL (5-Matrix & 3-Matrix)
  if (
    type === "LEVEL_BONUS" ||
    type === "AUTOPOOL_BONUS_FIVE" ||
    type === "AUTOPOOL_BONUS_THREE" ||
    type === "PRIME_150_SELF" ||
    type === "PRIME_750_SELF" ||
    type === "PRIME_759_SELF" ||
    src.startsWith("THREE_MATRIX") ||
    src.startsWith("FIVE_MATRIX") ||
    (type === "INCOME_CREDIT_75" && (
      ot.includes("LEVEL") ||
      ot.includes("AUTOPOOL") ||
      src.includes("MATRIX") ||
      trig === "PRIME_150" ||
      trig === "PRIME_750" ||
      trig === "PRIME_759"
    ))
  ) {
    return "LAYER";
  }

  // 4. DIRECT BONUS
  if (
    type === "DIRECT_REF_BONUS" ||
    type === "MONTHLY_759_DIRECT" ||
    src.includes("REFERRAL") ||
    st.includes("REFERRAL") ||
    (st === "RANK_UPGRADE" && (ot.includes("DIRECT") || type.includes("DIRECT"))) ||
    (type === "INCOME_CREDIT_75" && (
      ot.includes("DIRECT") ||
      src.includes("DIRECT") ||
      trig === "PACKAGE_DIRECT" ||
      trig === "JOIN_REFERRAL"
    ))
  ) {
    return "DIRECT";
  }

  // 4. P2P TRANSFERS
  if (
    type === "P2P_PACKAGE_COUPON_SEND" ||
    type === "P2P_PACKAGE_COUPON_RECEIVE" ||
    type === "SELF_PACKAGE_COUPON_CREATE" ||
    type === "MAIN_TO_COUPON" ||
    type === "COUPON_WALLET_TRANSFER_OUT" ||
    type === "COUPON_PURCHASE_CREDIT" ||
    type === "VOUCHER_CREATE_DEBIT" ||
    type.includes("P2P")
  ) {
    return "P2P";
  }

  // 5. ROYALTY BONUS
  if (
    type === "GLOBAL_ROYALTY" ||
    type === "ROYALTY_BONUS" ||
    type === "GLOBAL_ACTIVATION_CREDIT" ||
    st === "DAILY_POOL_DISTRIBUTION" ||
    src.includes("ROYALTY") ||
    ot.includes("ROYALTY") ||
    String(tx?.source_id || "").toUpperCase().startsWith("ROYALTY") ||
    meta.club
  ) {
    return "ROYALTY";
  }

  // 6. MERCHANT, CAPTAIN & FRANCHISE
  if (
    type === "FRANCHISE_INCOME" ||
    type === "CAPTAIN_INCOME" ||
    type === "COMMISSION_CREDIT" ||
    src.includes("FRANCHISE") ||
    src.includes("CAPTAIN") ||
    src.includes("MERCHANT") ||
    src.includes("ZONAL") ||
    st.includes("FRANCHISE")
  ) {
    return "MERCHANT_CAPTAIN";
  }

  // 7. WITHDRAWALS
  if (
    type === "WITHDRAWAL_DEBIT" ||
    type === "WITHDRAWABLE_CREDIT" ||
    type === "INTERNAL_WALLET_DEBIT" ||
    type === "WITHDRAWAL_REFUND" ||
    src.includes("WITHDRAW")
  ) {
    return "WITHDRAWAL";
  }

  // 8. REDEEM & COUPONS
  if (
    type.includes("ECOUPON") ||
    type.includes("VOUCHER") ||
    type === "REDEEM_ECOUPON_CREDIT" ||
    type === "AUTO_ECOUPON_ISSUED" ||
    type === "AUTO_PURCHASE_DEBIT" ||
    type.startsWith("RP_")
  ) {
    return "REDEEM";
  }

  return "OTHER";
}

const HistoryRow = React.memo(function HistoryRow({ tx, onClick }) {
  const rawAmount = Number(tx?.amount || 0);
  const meta = tx?.meta || {};
  const isP2pReceive = tx?.type === "P2P_PACKAGE_COUPON_RECEIVE";
  const amount = isP2pReceive && rawAmount === 0
    ? Number(meta.net_amount ?? meta.gross_amount ?? 0)
    : rawAmount;
  const isCouponUnredeemed = isP2pReceive && (meta.status === "VOUCHER_ASSIGNED" || !meta.redeemed_at);

  const isSelfAccount =
    tx?.type === "SELF_ACCOUNT_CREDIT" ||
    tx?.type === "SELF_ACCOUNT_DEBIT" ||
    meta?.ledger === "SELF_ACCOUNT" ||
    String(tx?.type || "").startsWith("SELF_ACCOUNT");

  const isSplitMainWalletCredit =
    tx?.type === "INCOME_CREDIT_75" ||
    meta?.split === "STREAM_75_25" ||
    (meta?.ledger === "MAIN" && meta?.income_75 !== undefined);

  const isPocketCredit =
    tx?.type === "WITHDRAWAL_WALLET_CREDIT" ||
    tx?.type === "COUPON_WALLET_CREDIT" ||
    tx?.type === "SHOPPING_WALLET_CREDIT" ||
    tx?.type === "INTERNAL_WALLET_CREDIT" ||
    tx?.type === "WITHDRAWAL_REFUND" ||
    meta?.type === "WITHDRAWAL_REFUND";

  const isMainWalletCredit =
    isSplitMainWalletCredit ||
    (amount > 0 && !isSelfAccount && !isP2pReceive && !isPocketCredit);

  const dateStr = tx?.created_at
    ? new Intl.DateTimeFormat(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(tx.created_at))
    : "-";

  const timeStr = tx?.created_at
    ? new Intl.DateTimeFormat(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(tx.created_at))
    : "";

  const levelVal = Number(tx?.meta?.level_index ?? tx?.meta?.level);
  const isRankUpgradeRow =
    String(tx?.source_type || "").toUpperCase() === "RANK_UPGRADE" ||
    String(tx?.meta?.kind || "").toUpperCase().startsWith("RANK_UPGRADE_");
  const rawSource = describeSource(tx) || humanizeType(tx?.type) || "Wallet Transfer";
  const typeName = isRankUpgradeRow
    ? rawSource
    : `${rawSource}${Number.isFinite(levelVal) && levelVal > 0 ? ` - Layer ${levelVal}` : ""}`;

  const cat = classifyTransaction(tx);
  const isCredit = amount >= 0;

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 1.4,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: isSelfAccount ? "#FDE68A" : "#EEF2F6",
        bgcolor: isSelfAccount ? "#FFFDF5" : "#FFFFFF",
        cursor: onClick ? "pointer" : "default",
        transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.02)",
        "&:hover": onClick
          ? {
              borderColor: isSelfAccount ? "#F59E0B" : "#CBD5E1",
              boxShadow: "0 4px 14px rgba(12, 45, 72, 0.06)",
              transform: "translateY(-1px)",
            }
          : {},
        "&:active": { transform: "scale(0.99)" },
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center">
        <RowIcon value={amount} tx={tx} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: 13.5,
                lineHeight: 1.25,
                color: "#0F172A",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              {typeName}
            </Typography>

            {/* Dedicated Term 2 Badge - Strictly only shown for Term 2 and higher, never for Term 1 */}
            {Number(tx?.term) >= 2 && (
              <Chip
                size="small"
                label={`Term ${tx.term} ⚡`}
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 900,
                  borderRadius: 1,
                  bgcolor: "#EFF6FF",
                  color: "#2563EB",
                  border: "1px solid #93C5FD",
                }}
              />
            )}

            {/* Dedicated Primary Badge for Wallet Split Type */}
            {isSelfAccount ? (
              <Chip
                size="small"
                label="Self Account Credit"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 800,
                  borderRadius: 1,
                  bgcolor: "#FEF3C7",
                  color: "#92400E",
                  border: "1px solid #FCD34D",
                }}
              />
            ) : isMainWalletCredit ? (
              <Chip
                size="small"
                label="Main Wallet Credit"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 800,
                  borderRadius: 1,
                  bgcolor: "#DCFCE7",
                  color: "#166534",
                  border: "1px solid #86EFAC",
                }}
              />
            ) : cat && cat !== "OTHER" && (
              <Chip
                size="small"
                label={CAT_CHIP_LABELS[cat] || cat}
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 700,
                  borderRadius: 1,
                  bgcolor: cat === "E_EDU" ? "#EDE9FE" : isCredit ? "#F0FDF4" : "#FEF2F2",
                  color: cat === "E_EDU" ? "#6D28D9" : isCredit ? "#166534" : "#991B1B",
                  border: cat === "E_EDU" ? "1px solid #DDD6FE" : undefined,
                }}
              />
            )}

            {isP2pReceive && (
              <Chip
                size="small"
                label={isCouponUnredeemed ? "Not Redeemed" : "Redeemed"}
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 800,
                  borderRadius: 1,
                  bgcolor: isCouponUnredeemed ? "#FEF3C7" : "#DCFCE7",
                  color: isCouponUnredeemed ? "#92400E" : "#166534",
                  border: isCouponUnredeemed ? "1px solid #FCD34D" : "1px solid #86EFAC",
                }}
              />
            )}
          </Box>

          <Typography sx={{ fontSize: 12, color: isSelfAccount ? "#B45309" : isMainWalletCredit ? "#047857" : "text.secondary", fontWeight: 700, mt: 0.2 }}>
            {isSelfAccount ? "25% Repurchase Self Account" : isSplitMainWalletCredit ? "75% Withdrawable Main Wallet" : isMainWalletCredit ? "Main Wallet Credit" : ""}
            {counterpartyLabel(tx) ? ` • ${counterpartyLabel(tx)}` : ""}
          </Typography>

          <Typography sx={{ fontSize: 11.5, color: "#94A3B8", mt: 0.25 }}>
            {dateStr} {timeStr ? `• ${timeStr}` : ""}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.8} alignItems="center">
          <Box sx={{ textAlign: "right" }}>
            <AmountBadge value={amount} />
          </Box>
          <ChevronRightIcon sx={{ color: "#CBD5E1", fontSize: 18 }} />
        </Stack>
      </Stack>
    </Paper>
  );
});

function TxDetailDrawer({ open, onClose, tx }) {
  if (!tx) return null;
  const rawAmount = Number(tx?.amount || 0);
  const meta = tx?.meta || {};
  const isP2pReceive = tx?.type === "P2P_PACKAGE_COUPON_RECEIVE";
  const amount = isP2pReceive && rawAmount === 0
    ? Number(meta.net_amount ?? meta.gross_amount ?? 0)
    : rawAmount;
  const isCredit = amount >= 0;
  const isCouponUnredeemed = isP2pReceive && (meta.status === "VOUCHER_ASSIGNED" || !meta.redeemed_at);
  const cat = classifyTransaction(tx);

  const dateStr = tx?.created_at
    ? new Intl.DateTimeFormat(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(tx.created_at))
    : "-";

  const grossVal = meta.gross_amount ?? meta.gross;
  const feeVal = meta.fee_amount ?? meta.admin_fee ?? meta.tax_amount;
  const netVal = meta.net_amount;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          p: { xs: 2, sm: 2.5 },
          pb: { xs: 4, sm: 3.5 },
          maxWidth: 560,
          mx: "auto",
          bgcolor: "#FFFFFF",
          boxShadow: "0 -12px 40px rgba(15,23,42,0.18)",
        },
      }}
    >
      <Box sx={{ width: 40, height: 4, bgcolor: "#CBD5E1", borderRadius: 2, mx: "auto", mb: 2 }} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontSize: 16.5, fontWeight: 900, color: "#0F172A" }}>
          Transaction Details
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2.5,
          bgcolor: isCredit ? "#F0FDF4" : "#FEF2F2",
          border: "1px solid",
          borderColor: isCredit ? "#BBF7D0" : "#FECACA",
          textAlign: "center",
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isCredit ? "#166534" : "#991B1B", mb: 0.3 }}>
          {describeSource(tx)}
        </Typography>
        <Typography sx={{ fontSize: 26, fontWeight: 900, color: isCredit ? "#15803D" : "#B91C1C" }}>
          {isCredit ? "+" : "-"}₹ {fmtAmount(Math.abs(amount))}
        </Typography>
        <Chip
          size="small"
          label={CAT_CHIP_LABELS[cat] || cat.replace(/_/g, " ")}
          sx={{
            mt: 0.8,
            height: 20,
            fontSize: 10.5,
            fontWeight: 800,
            bgcolor: cat === "E_EDU" ? "#EDE9FE" : isCredit ? "#DCFCE7" : "#FEE2E2",
            color: cat === "E_EDU" ? "#6D28D9" : isCredit ? "#166534" : "#991B1B",
            border: cat === "E_EDU" ? "1px solid #DDD6FE" : undefined,
          }}
        />
      </Paper>

      <Stack spacing={1.2} sx={{ bgcolor: "#F8FAFC", p: 1.8, borderRadius: 2, border: "1px solid #E2E8F0" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Transaction ID</Typography>
          <Typography sx={{ fontSize: 12, color: "#0F172A", fontWeight: 800 }}>#{tx.id || "N/A"}</Typography>
        </Box>
        <Divider />

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Timestamp</Typography>
          <Typography sx={{ fontSize: 12, color: "#0F172A", fontWeight: 700 }}>{dateStr}</Typography>
        </Box>
        <Divider />

        {Number(tx?.term) >= 2 && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Academic Term</Typography>
              <Chip
                size="small"
                label={`Term ${tx.term} (Active Current Term ⚡)`}
                sx={{
                  height: 20,
                  fontSize: 10.5,
                  fontWeight: 900,
                  bgcolor: "#EFF6FF",
                  color: "#2563EB",
                  border: "1px solid #93C5FD",
                }}
              />
            </Box>
            <Divider />
          </>
        )}

        {counterpartyLabel(tx) && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Counterparty</Typography>
              <Typography sx={{ fontSize: 12, color: "#2563EB", fontWeight: 800 }}>{counterpartyLabel(tx)}</Typography>
            </Box>
            <Divider />
          </>
        )}

        {isP2pReceive && meta.voucher_code && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Coupon Code</Typography>
              <Typography sx={{ fontSize: 12, color: "#2563EB", fontWeight: 800 }}>{meta.voucher_code}</Typography>
            </Box>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Coupon Status</Typography>
              <Typography sx={{ fontSize: 12, color: isCouponUnredeemed ? "#B45309" : "#166534", fontWeight: 800 }}>
                {isCouponUnredeemed ? "Active (Not Redeemed)" : "Redeemed"}
              </Typography>
            </Box>
            <Divider />
          </>
        )}

        {grossVal !== undefined && grossVal !== null && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Gross Amount</Typography>
              <Typography sx={{ fontSize: 12, color: "#0F172A", fontWeight: 800 }}>₹ {fmtAmount(grossVal)}</Typography>
            </Box>
            <Divider />
          </>
        )}

        {feeVal !== undefined && feeVal !== null && Number(feeVal) > 0 && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Platform Tax / Fee</Typography>
              <Typography sx={{ fontSize: 12, color: "#DC2626", fontWeight: 800 }}>-₹ {fmtAmount(feeVal)}</Typography>
            </Box>
            <Divider />
          </>
        )}

        {netVal !== undefined && netVal !== null && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Net Value</Typography>
              <Typography sx={{ fontSize: 12, color: "#059669", fontWeight: 900 }}>₹ {fmtAmount(netVal)}</Typography>
            </Box>
            <Divider />
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Status</Typography>
          <Typography sx={{ fontSize: 12, color: "#166534", fontWeight: 800 }}>Completed</Typography>
        </Box>
      </Stack>

      <Button
        fullWidth
        variant="contained"
        onClick={onClose}
        sx={{
          mt: 2,
          py: 1.1,
          borderRadius: 2,
          fontWeight: 800,
          textTransform: "none",
          bgcolor: "#0F172A",
          "&:hover": { bgcolor: "#1E293B" },
        }}
      >
        Done
      </Button>
    </Drawer>
  );
}

const SectionList = React.memo(function SectionList({ sections, fallbackRows = [], onRowClick }) {
  const [visibleLimit, setVisibleLimit] = useState(50);

  const empty =
    !sections ||
    sections.length === 0 ||
    sections.every((s) => !s.rows || s.rows.length === 0);

  if (empty) {
    if (Array.isArray(fallbackRows) && fallbackRows.length > 0) {
      const visibleFallback = fallbackRows.slice(0, visibleLimit);
      return (
        <Stack spacing={1}>
          {visibleFallback.map((tx, i) => (
            <HistoryRow
              key={`${tx.id || i}-${tx.created_at || i}`}
              tx={tx}
              onClick={() => onRowClick && onRowClick(tx)}
            />
          ))}
          {fallbackRows.length > visibleLimit && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setVisibleLimit((v) => v + 50)}
              sx={{ mt: 1, py: 1, borderRadius: 2, textTransform: "none", fontWeight: 700 }}
            >
              Load More Transactions ({fallbackRows.length - visibleLimit} remaining)
            </Button>
          )}
        </Stack>
      );
    }
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700 }}>
          No transactions found for this source or filter.
        </Typography>
      </Box>
    );
  }

  let rowsCounted = 0;
  const renderedSections = [];
  let totalRowsAcross = 0;

  for (const sec of sections) {
    const sRows = sec.rows || [];
    totalRowsAcross += sRows.length;
    if (rowsCounted >= visibleLimit) continue;
    const remainingBudget = visibleLimit - rowsCounted;
    const sliceOfRows = sRows.slice(0, remainingBudget);
    rowsCounted += sliceOfRows.length;
    renderedSections.push({
      ...sec,
      rows: sliceOfRows,
    });
  }

  return (
    <Stack spacing={1.5}>
      {renderedSections.map((sec, idx) => (
        <Box key={`${sec.title}-${idx}`}>
          <SectionHeader title={sec.title} total={sec.total} />
          <Stack spacing={1}>
            {sec.rows.map((tx, i) => (
              <HistoryRow
                key={`${tx.id || i}-${tx.created_at || i}`}
                tx={tx}
                onClick={() => onRowClick && onRowClick(tx)}
              />
            ))}
          </Stack>
        </Box>
      ))}
      {totalRowsAcross > visibleLimit && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => setVisibleLimit((v) => v + 100)}
          sx={{
            mt: 1.5,
            py: 1,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 800,
            borderColor: "#CBD5E1",
            color: "#334155",
            bgcolor: "#F8FAFC",
            "&:hover": { bgcolor: "#F1F5F9", borderColor: "#94A3B8" },
          }}
        >
          Load More Transactions ({totalRowsAcross - visibleLimit} remaining)
        </Button>
      )}
    </Stack>
  );
});

function PhonePeFilterDrawer({
  open,
  onClose,
  termFilter = "ALL",
  setTermFilter,
  purchasedTerms = [1],
  datePreset,
  setDatePreset,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  flowFilter,
  setFlowFilter,
  sourceFilter,
  setSourceFilter,
  onReset,
}) {
  const datePresets = [
    { label: "All Time", value: "all" },
    { label: "Today", value: "today" },
    { label: "This Month", value: "this_month" },
    { label: "Last Month", value: "last_month" },
    { label: "Last 30 Days", value: "30_days" },
    { label: "Custom Range", value: "custom" },
  ];

  const flowOptions = [
    { label: "All", value: "ALL" },
    { label: "Money Received (+ Credits)", value: "CREDIT" },
    { label: "Self Account", value: "DEBIT" },
  ];

  const sourceOptions = [
    { label: "All Sources", value: "ALL" },
    { label: "5-Matrix Layer", value: "FIVE_LAYER" },
    { label: "3-Matrix Layer", value: "THREE_LAYER" },
    { label: "E-Edu Layer", value: "E_EDU_LAYER" },
    { label: "E-Edu Direct Referral", value: "E_EDU_DIRECT" },
    { label: "E-Edu All", value: "E_EDU" },
    { label: "Self Account / Self Block", value: "SAVE" },
    { label: "SSV Layer", value: "SSV_LAYER" },
    { label: "SSV Bonus", value: "SSV_BONUS" },
    { label: "All Layers & Blocks", value: "LAYER" },
    { label: "Direct Bonus (All)", value: "DIRECT" },
    { label: "Same-Rank Direct (Uncapped)", value: "SAME_RANK_DIRECT" },
    { label: "Higher-Rank Direct (Cycle Capped)", value: "HIGHER_RANK_DIRECT" },
    { label: "Held Missing Income (7-Day)", value: "MISSING_INCOME" },
    { label: "QR Scanner", value: "QR_SCANNER" },
    { label: "Trizone Shopping", value: "TRIZONE" },
    { label: "Royalty Bonus", value: "ROYALTY" },
    { label: "Gift Voucher", value: "P2P" },
    { label: "Merchant & Captain", value: "MERCHANT_CAPTAIN" },
    { label: "Withdrawals", value: "WITHDRAWAL" },
  ];

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          p: { xs: 2, sm: 2.5 },
          pb: { xs: 4, sm: 3.5 },
          maxWidth: 560,
          mx: "auto",
          bgcolor: "#FFFFFF",
          boxShadow: "0 -12px 40px rgba(15,23,42,0.18)",
          maxHeight: "85vh",
          overflowY: "auto",
        },
      }}
    >
      <Box sx={{ width: 40, height: 4, bgcolor: "#CBD5E1", borderRadius: 2, mx: "auto", mb: 2 }} />

      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 900, color: "#0F172A" }}>
            Filter Transactions
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#64748B" }}>
            PhonePe-style academic term, date range & category filters
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            onClick={onReset}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: "#DC2626" }}
          >
            Reset All
          </Button>
          <IconButton size="small" onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Section 0: Academic Term Filter (Strictly bounded to purchased terms) */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#1E293B", mb: 1 }}>
          🎓 Academic Term
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
          {[
            { label: "All Terms", value: "ALL" },
            { label: "Term 1", value: "1" },
            ...(purchasedTerms.includes(2) ? [{ label: "Term 2", value: "2" }] : []),
            ...(purchasedTerms.includes(3) ? [{ label: "Term 3", value: "3" }] : []),
          ].map((t) => {
            const isSelected = String(termFilter) === String(t.value);
            return (
              <Chip
                key={t.value}
                label={t.label}
                onClick={() => setTermFilter && setTermFilter(t.value)}
                sx={{
                  fontWeight: 800,
                  fontSize: 12,
                  borderRadius: 999,
                  bgcolor: isSelected ? "#064E3B" : "#F1F5F9",
                  color: isSelected ? "#FFFFFF" : "#475569",
                  border: "1px solid",
                  borderColor: isSelected ? "#064E3B" : "#E2E8F0",
                  cursor: "pointer",
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Section 1: Date Range */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#1E293B", mb: 1 }}>
          📅 Date Range
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 1.2 }}>
          {datePresets.map((p) => {
            const isSelected = datePreset === p.value;
            return (
              <Chip
                key={p.value}
                label={p.label}
                onClick={() => setDatePreset(p.value)}
                sx={{
                  fontWeight: 800,
                  fontSize: 12,
                  borderRadius: 999,
                  bgcolor: isSelected ? "#2563EB" : "#F1F5F9",
                  color: isSelected ? "#FFFFFF" : "#475569",
                  border: "1px solid",
                  borderColor: isSelected ? "#2563EB" : "#E2E8F0",
                  cursor: "pointer",
                }}
              />
            );
          })}
        </Box>

        {/* Custom Date Pickers */}
        {datePreset === "custom" && (
          <Paper elevation={0} sx={{ p: 1.5, bgcolor: "#F8FAFC", borderRadius: 2, border: "1px solid #E2E8F0", mt: 1 }}>
            <Typography sx={{ fontSize: 11.5, color: "#64748B", fontWeight: 700, mb: 1 }}>
              Pick Start Date and End Date
            </Typography>
            <Stack direction="row" spacing={1.2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ bgcolor: "#fff" }}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ bgcolor: "#fff" }}
              />
            </Stack>
          </Paper>
        )}
      </Box>

      {/* Section 2: Transaction Flow */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#1E293B", mb: 1 }}>
          ↕️ Transaction Flow
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
          {flowOptions.map((f) => {
            const isSelected = flowFilter === f.value;
            return (
              <Chip
                key={f.value}
                label={f.label}
                onClick={() => setFlowFilter(f.value)}
                sx={{
                  fontWeight: 800,
                  fontSize: 12,
                  borderRadius: 999,
                  bgcolor: isSelected ? "#0F172A" : "#F1F5F9",
                  color: isSelected ? "#FFFFFF" : "#475569",
                  border: "1px solid",
                  borderColor: isSelected ? "#0F172A" : "#E2E8F0",
                  cursor: "pointer",
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Section 3: Income Source / Category */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#1E293B", mb: 1 }}>
          🏷️ Source Category
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
          {sourceOptions.map((s) => {
            const isSelected = sourceFilter === s.value;
            return (
              <Chip
                key={s.value}
                label={s.label}
                onClick={() => setSourceFilter(s.value)}
                sx={{
                  fontWeight: 800,
                  fontSize: 12,
                  borderRadius: 999,
                  bgcolor: isSelected ? "#2563EB" : "#F1F5F9",
                  color: isSelected ? "#FFFFFF" : "#475569",
                  border: "1px solid",
                  borderColor: isSelected ? "#2563EB" : "#E2E8F0",
                  cursor: "pointer",
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Apply Button */}
      <Button
        fullWidth
        variant="contained"
        onClick={onClose}
        sx={{
          py: 1.3,
          borderRadius: 2.5,
          fontWeight: 800,
          fontSize: 14,
          textTransform: "none",
          bgcolor: "#2563EB",
          "&:hover": { bgcolor: "#1D4ED8" },
        }}
      >
        Apply Filters
      </Button>
    </Drawer>
  );
}

const P2PTransferPanel = React.memo(function P2PTransferPanel({
  p2pTaxPercent = 7,
  selfTaxPercent = 7,
  availableBalance = 0,
  onSuccess,
}) {
  const [transferMode, setTransferMode] = useState("MEMBER"); // "MEMBER" | "SELF"
  const isSelf = transferMode === "SELF";
  const [recipientPhone, setRecipientPhone] = useState("");
  const [category, setCategory] = useState("E-Edu Package Purchase");
  const [amount, setAmount] = useState("2000");
  const [categories, setCategories] = useState([
    { id: "E_EDU", label: "E-Edu Package Purchase", has_presets: true },
    { id: "TRI_HOLIDAYS", label: "Tri Holidays", has_presets: false },
  ]);
  const [recipientValid, setRecipientValid] = useState(null);
  const [recipientChecking, setRecipientChecking] = useState(false);
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [p2pBusy, setP2pBusy] = useState(false);
  const [p2pError, setP2pError] = useState("");
  const [p2pSuccess, setP2pSuccess] = useState("");

  const currentUsername = useMemo(() => {
    try {
      const ls =
        localStorage.getItem("user_user") ||
        sessionStorage.getItem("user_user") ||
        localStorage.getItem("user") ||
        sessionStorage.getItem("user");
      const u = ls ? JSON.parse(ls) : null;
      return String(u?.username || "").trim();
    } catch {
      return "";
    }
  }, []);

  useEffect(() => {
    let unmounted = false;
    API.get("/business/coupons/categories/")
      .then((res) => {
        if (!unmounted && res?.data?.categories && res.data.categories.length > 0) {
          setCategories(res.data.categories);
        }
      })
      .catch(() => {});
    return () => {
      unmounted = true;
    };
  }, []);

  const handleVerify = async () => {
    const val = String(recipientPhone || "").trim();
    if (!val) {
      setRecipientValid(false);
      setRecipientInfo(null);
      return;
    }
    setRecipientChecking(true);
    setRecipientValid(null);
    setRecipientInfo(null);
    try {
      let found = false;
      let info = null;

      try {
        const res = await API.get("/accounts/regions/by-sponsor/", {
          params: { sponsor: val, level: "state" },
        });
        const sp = res?.data?.sponsor;
        if (sp && sp.username) {
          found = true;
          info = {
            name: sp.full_name || sp.name || sp.username,
            username: sp.username,
            phone: sp.phone || sp.username,
            pincode: sp.pincode,
          };
        }
      } catch (_) {}

      if (!found) {
        try {
          const res2 = await API.get("/accounts/hierarchy/", {
            params: { username: val },
          });
          const u = res2?.data?.user || res2?.data;
          if (u && (u.username || u.full_name)) {
            found = true;
            info = {
              name: u.full_name || u.username,
              username: u.username,
              phone: u.phone || u.username,
              pincode: u.pincode,
            };
          }
        } catch (_) {}
      }

      if (found && info) {
        setRecipientValid(true);
        setRecipientInfo(info);
      } else {
        setRecipientValid(false);
        setRecipientInfo(null);
      }
    } catch (_) {
      setRecipientValid(false);
      setRecipientInfo(null);
    } finally {
      setRecipientChecking(false);
    }
  };

  const p2pGross = Number(amount || 0);
  const effectiveTaxPercent = isSelf ? Number(selfTaxPercent || 7) : Number(p2pTaxPercent || 7);
  const p2pFee = p2pGross > 0 ? Number(((p2pGross * effectiveTaxPercent) / 100).toFixed(2)) : 0;
  const p2pTotalDebit = Number((p2pGross + p2pFee).toFixed(2));

  const [otpModalOpen, setOtpModalOpen] = useState(false);

  const handleTransfer = () => {
    if (!isSelf && !recipientPhone.trim()) {
      setP2pError("Please enter recipient phone number or user ID.");
      return;
    }
    if (!isSelf && recipientValid !== true) {
      setP2pError("Please verify recipient before sending gift voucher.");
      return;
    }
    if (p2pGross <= 0) {
      setP2pError("Please select or enter a valid voucher amount.");
      return;
    }
    if (p2pTotalDebit > availableBalance) {
      const feeDesc = p2pFee > 0 ? ` (Face Value: ₹${fmtAmount(p2pGross)} + ${effectiveTaxPercent}% Fee: ₹${fmtAmount(p2pFee)})` : "";
      setP2pError(`Insufficient balance. Total required is ₹${fmtAmount(p2pTotalDebit)}${feeDesc}. Your available balance is ₹${fmtAmount(availableBalance)}.`);
      return;
    }
    setOtpModalOpen(true);
  };

  const handleConfirmP2pOtp = async (otpCode) => {
    try {
      setP2pBusy(true);
      setP2pError("");
      setP2pSuccess("");

      const res = await API.post("/business/coupons/p2p-transfer/", {
        transfer_mode: isSelf ? "SELF" : "MEMBER",
        recipient_phone: isSelf ? (currentUsername || "") : recipientPhone.trim(),
        amount: p2pGross,
        category: category || "E-Edu Package Purchase",
        coupon_type: "PACKAGE_COUPON",
        otp: otpCode,
      });

      const vCode = res.data?.voucher_code;
      const successMsg = isSelf
        ? `Successfully converted ₹${fmtAmount(p2pGross)} to Self Package Coupon!${vCode ? ` • Voucher Code: ${vCode}` : ""}`
        : `Successfully sent ₹${fmtAmount(p2pGross)} Gift Voucher to ${recipientPhone}! (Debited ₹${fmtAmount(p2pTotalDebit)} including ${p2pTaxPercent}% fee)${vCode ? ` • Voucher Code: ${vCode}` : ""}`;
      setP2pSuccess(successMsg);
      setTransferMode("MEMBER");
      setRecipientPhone("");
      setAmount("2000");
      setRecipientValid(null);
      setRecipientInfo(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || "Failed to complete Gift Voucher send.";
      setP2pError(msg);
      throw err;
    } finally {
      setP2pBusy(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#F8FAFC", p: 2, borderRadius: 3, border: "1px solid #E2E8F0", mb: 2 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 900, color: "#0F172A", mb: 0.5 }}>
        🎁 Send Gift Voucher
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#64748B", mb: 1 }}>
        Recipient receives 100% of the package face value. A <b>{isSelf ? selfTaxPercent : p2pTaxPercent}% platform fee</b> is added to your debit.
      </Typography>
      <Box sx={{ mb: 1.5, p: 1, bgcolor: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 2 }}>
        <Typography sx={{ fontSize: 12, color: "#991B1B", fontWeight: 700 }}>
          ⏱️ 48-Hour (2-Day) Validity Window: Vouchers must be redeemed within 48 hours. After 48 hours, unredeemed vouchers expire and cannot be refunded.
        </Typography>
      </Box>

      {p2pError && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, fontSize: 12.5 }}>{p2pError}</Alert>}
      {p2pSuccess && <Alert severity="success" sx={{ mb: 1.5, borderRadius: 2, fontSize: 12.5 }}>{p2pSuccess}</Alert>}

      <Stack spacing={1.5}>
        <TextField
          select
          fullWidth
          size="small"
          label="Transfer Destination"
          value={transferMode}
          onChange={(e) => {
            const mode = e.target.value;
            setTransferMode(mode);
            if (mode === "SELF") {
              setRecipientPhone(currentUsername);
              setRecipientValid(true);
            } else {
              setRecipientPhone("");
              setRecipientValid(null);
            }
            setP2pError("");
          }}
          sx={{ bgcolor: "#fff", borderRadius: 2 }}
        >
          <MenuItem value="MEMBER">👥 Send to Member (+{p2pTaxPercent}% Fee)</MenuItem>
          <MenuItem value="SELF">👤 Self Transfer (+{selfTaxPercent}% Fee • E-Edu Package Voucher)</MenuItem>
        </TextField>

        {isSelf ? (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: "#EFF6FF",
              border: "1px solid #BFDBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: "#DBEAFE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
              }}
            >
              👤
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#1E3A8A" }}>
                Self Package Voucher ({selfTaxPercent}% Fee)
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "#2563EB", lineHeight: 1.4 }}>
                Instant voucher assigned directly to your account ({currentUsername || "You"}) for course & stage activations on <b>trieducation.in</b>.
              </Typography>
            </Box>
          </Paper>
        ) : (
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                fullWidth
                size="small"
                label="Recipient Phone / User ID"
                placeholder="Enter 10-digit mobile number"
                value={recipientPhone}
                onChange={(e) => {
                  setRecipientPhone(e.target.value);
                  setP2pError("");
                  setRecipientValid(null);
                  setRecipientInfo(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleVerify();
                  }
                }}
                inputProps={{ maxLength: 15 }}
                sx={{ bgcolor: "#fff", borderRadius: 2 }}
              />
              <Button
                variant="contained"
                disabled={recipientChecking || !recipientPhone.trim()}
                onClick={handleVerify}
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "12.5px",
                  bgcolor: "#0F172A",
                  "&:hover": { bgcolor: "#1E293B" },
                  whiteSpace: "nowrap",
                  minWidth: 80,
                }}
              >
                {recipientChecking ? <CircularProgress size={14} color="inherit" /> : "Verify"}
              </Button>
            </Stack>

            {!recipientChecking && recipientValid === true && recipientInfo && (
              <Paper
                elevation={0}
                sx={{
                  mt: 1,
                  p: 1,
                  px: 1.2,
                  borderRadius: 2,
                  bgcolor: "#ECFDF5",
                  border: "1px solid #A7F3D0",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CheckRoundedIcon sx={{ color: "#059669", fontSize: 18 }} />
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#065F46" }}>
                    Verified: {recipientInfo.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#047857" }}>
                    ID/Phone: {recipientInfo.username} {recipientInfo.pincode ? `• PIN: ${recipientInfo.pincode}` : ""}
                  </Typography>
                </Box>
              </Paper>
            )}

            {!recipientChecking && recipientValid === false && (
              <Typography sx={{ fontSize: 11.5, color: "#DC2626", fontWeight: 600, mt: 0.8, px: 0.5 }}>
                ⚠ Recipient not found. Please verify the mobile number or user ID.
              </Typography>
            )}
          </Box>
        )}

        <TextField
          select
          label="Voucher Category"
          fullWidth
          size="small"
          value={category}
          onChange={(e) => {
            const nextLabel = e.target.value;
            const catObj = categories.find((c) => c.label === nextLabel || c.id === nextLabel);
            setCategory(nextLabel);
            setAmount(catObj?.has_presets ? "2000" : "");
            setP2pError("");
          }}
          sx={{ bgcolor: "#fff", borderRadius: 2 }}
        >
          {categories.map((c) => (
            <MenuItem key={c.id || c.label} value={c.label}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>

        {(() => {
          const currentCat = categories.find((c) => c.label === category || c.id === category);
          const showPresets = currentCat ? Boolean(currentCat.has_presets) : category === "E-Edu Package Purchase";

          if (showPresets) {
            return (
              <Box>
                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#475569", mb: 0.8 }}>
                  Select Preset Voucher Package:
                </Typography>
                <Grid container spacing={1}>
                  {[2000, 3750, 4250, 5000, 8000, 10000, 25000].map((amt) => {
                    const isSel = Number(amount) === amt;
                    return (
                      <Grid item xs={amt >= 10000 ? 6 : 4} key={amt}>
                        <Button
                          fullWidth
                          variant={isSel ? "contained" : "outlined"}
                          onClick={() => {
                            setAmount(String(amt));
                            setP2pError("");
                          }}
                          sx={{
                            py: 0.8,
                            fontSize: "12px",
                            fontWeight: 800,
                            borderRadius: 2,
                            bgcolor: isSel ? "#2563EB" : "#FFFFFF",
                            color: isSel ? "#FFFFFF" : "#1E293B",
                            borderColor: isSel ? "#2563EB" : "#CBD5E1",
                            "&:hover": {
                              bgcolor: isSel ? "#1D4ED8" : "#F1F5F9",
                            },
                          }}
                        >
                          ₹{amt.toLocaleString("en-IN")}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            );
          }

          return (
            <TextField
              label={`${category || "Voucher"} Amount (₹)`}
              type="number"
              fullWidth
              size="small"
              placeholder="Enter amount (e.g. 5000)"
              value={amount}
              inputProps={{ min: 1 }}
              onChange={(e) => {
                setAmount(e.target.value);
                setP2pError("");
              }}
              sx={{ bgcolor: "#fff", borderRadius: 2 }}
            />
          );
        })()}

        {p2pGross > 0 && (
          <Paper elevation={0} sx={{ p: 1.5, bgcolor: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 2 }}>
            <Stack spacing={0.5}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 12, color: "#1E3A8A" }}>Package Face Value:</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1E3A8A" }}>₹ {fmtAmount(p2pGross)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 12, color: "#2563EB" }}>
                  Platform Fee (+{effectiveTaxPercent}%):
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#2563EB" }}>
                  + ₹ {fmtAmount(p2pFee)}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.5, borderColor: "#BFDBFE" }} />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#1D4ED8" }}>Total Debited from You:</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#DC2626" }}>₹ {fmtAmount(p2pTotalDebit)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#16A34A" }}>
                  {isSelf ? "Voucher Issued to You:" : "Recipient Receives:"}
                </Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "#16A34A" }}>₹ {fmtAmount(p2pGross)} Voucher</Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        <Button
          fullWidth
          variant="contained"
          disabled={p2pBusy || p2pGross <= 0 || (!isSelf && (!recipientPhone.trim() || recipientValid !== true))}
          onClick={handleTransfer}
          startIcon={p2pBusy ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
          sx={{
            py: 1.25,
            borderRadius: 2.5,
            fontWeight: 800,
            fontSize: 14,
            textTransform: "none",
            bgcolor: "#2563EB",
            "&:hover": { bgcolor: "#1D4ED8" },
          }}
        >
          {p2pBusy
            ? "Processing..."
            : isSelf
            ? `Convert ₹${fmtAmount(p2pGross)} to Self Coupon`
            : recipientValid === true
            ? `Send ₹${fmtAmount(p2pGross)} Gift Voucher`
            : "Verify Recipient to Send"}
        </Button>
      </Stack>

      {/* Email OTP Verification Modal */}
      <EmailOtpModal
        open={otpModalOpen}
        onClose={() => setOtpModalOpen(false)}
        title={isSelf ? "Authorize Self Package Coupon" : "Authorize Gift Voucher Send"}
        actionLabel={isSelf ? "Confirm & Convert" : "Confirm & Send"}
        transferType="p2p_transfer"
        amount={p2pGross}
        recipientPhone={isSelf ? (currentUsername || "Self") : recipientPhone.trim()}
        details={
          isSelf
            ? [
                { label: "Voucher Category", value: category },
                { label: "Package Face Value", value: `₹${fmtAmount(p2pGross)}` },
                { label: "Destination", value: `Self Account (${currentUsername || "You"})` },
                { label: `Platform Fee (+${effectiveTaxPercent}%)`, value: `+ ₹${fmtAmount(p2pFee)}`, color: "#2563EB" },
                { label: "Total Debited from You", value: `₹${fmtAmount(p2pTotalDebit)}`, color: "#DC2626" },
                { label: "Validity Window", value: "48 Hours (Expires if unredeemed • Non-refundable)", color: "#DC2626" },
              ]
            : [
                { label: "Voucher Category", value: category },
                { label: "Package Face Value", value: `₹${fmtAmount(p2pGross)}` },
                { label: "Recipient", value: recipientInfo ? `${recipientInfo.name || recipientInfo.username} (${recipientPhone})` : recipientPhone },
                { label: `Platform Fee (+${p2pTaxPercent}%)`, value: `+ ₹${fmtAmount(p2pFee)}`, color: "#2563EB" },
                { label: "Total Debited from You", value: `₹${fmtAmount(p2pTotalDebit)}`, color: "#DC2626" },
                { label: "Validity Window", value: "48 Hours (Expires if unredeemed • Non-refundable)", color: "#DC2626" },
              ]
        }
        onConfirm={handleConfirmP2pOtp}
      />
    </Box>
  );
});

/** ---------- main page ---------- */
export default function History() {
  const navigate = useNavigate();
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user_user") || sessionStorage.getItem("user_user") || localStorage.getItem("user");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [actionDrawerOpen, setActionDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("p2p"); // "p2p" | "redeem" | "menu"

  const [top, setTop] = useState({
    main_income_balance: "0.00",
    self_account_balance: "0.00",
    withdrawable_balance: "0.00",
    shopping_rewards_points: "0.00",
    redeem_points: "0.00",
    all_earnings_total: "0.00",
  });

  const [mainWallet, setMainWallet] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [selfAccount, setSelfAccount] = useState([]);
  const [cashback, setCashback] = useState([]);
  const [redeem, setRedeem] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("ALL");
  const [selectedTx, setSelectedTx] = useState(null);
  const [txDetailOpen, setTxDetailOpen] = useState(false);
  const [tab, setTab] = useState(0);

  // Academic Term Filter State (Strictly bounded to database-purchased terms)
  const [termFilter, setTermFilter] = useState("ALL"); // "ALL" | "1" | "2" ...
  const [purchasedTerms, setPurchasedTerms] = useState([1]);
  const [currentTerm, setCurrentTerm] = useState(1);
  const [dbTermStats, setDbTermStats] = useState({});

  // PhonePe-Style Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [flowFilter, setFlowFilter] = useState("ALL"); // "ALL" | "CREDIT" | "DEBIT"
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [dbCategoryStats, setDbCategoryStats] = useState(null);
  const [filteredStats, setFilteredStats] = useState({ count: 0, total: "0.00" });

  const fetchHistory = useCallback(async () => {
    try {
      setErr("");
      const params = { limit: 2000 };
      if (termFilter && termFilter !== "ALL") {
        params.term = termFilter;
      }
      if (sourceFilter && sourceFilter !== "ALL") {
        params.category = sourceFilter;
      }
      if (flowFilter && flowFilter !== "ALL") {
        params.flow = flowFilter;
      }
      if (datePreset && datePreset !== "all") {
        params.date_preset = datePreset;
      }
      if (customStartDate) {
        params.start_date = customStartDate;
      }
      if (customEndDate) {
        params.end_date = customEndDate;
      }
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      const res = await API.get("/accounts/wallet/me/history/", { params });
      const data = res?.data || {};

      if (data?.current_term) {
        setCurrentTerm(Number(data.current_term));
      }
      if (data?.purchased_terms && Array.isArray(data.purchased_terms)) {
        setPurchasedTerms(data.purchased_terms);
      } else {
        try {
          const userCycle = JSON.parse(
            localStorage.getItem(`trikonekt_cycle_user_${currentUser?.id}`) ||
            localStorage.getItem("trikonekt_consumer_cycle_state") || "{}"
          );
          const c = Number(userCycle.currentCycle || 1);
          const p = [1];
          for (let i = 2; i <= c; i++) p.push(i);
          setPurchasedTerms(p);
        } catch (_) {}
      }
      if (data?.term_stats) {
        setDbTermStats(data.term_stats);
      }

      if (data?.category_stats) {
        setDbCategoryStats(data.category_stats);
      }
      if (data?.filtered_stats) {
        setFilteredStats(data.filtered_stats);
      }

      setTop({
        main_income_balance: data?.top?.main_income_balance ?? "0.00",
        self_account_balance: data?.top?.self_account_balance ?? "0.00",
        withdrawable_balance: data?.top?.withdrawable_balance ?? "0.00",
        shopping_rewards_points: data?.top?.shopping_rewards_points ?? "0.00",
        redeem_points: data?.top?.redeem_points ?? "0.00",
        all_earnings_total: data?.top?.all_earnings_total ?? "0.00",
        all_earnings_count: data?.top?.all_earnings_count ?? 0,
        today_bonus_100: data?.top?.today_bonus_100 ?? "0.00",
        yesterday_bonus_100: data?.top?.yesterday_bonus_100 ?? "0.00",
        today_main_75: data?.top?.today_main_75 ?? "0.00",
        yesterday_main_75: data?.top?.yesterday_main_75 ?? "0.00",
        today_self_25: data?.top?.today_self_25 ?? "0.00",
        yesterday_self_25: data?.top?.yesterday_self_25 ?? "0.00",
        lifetime_credits_total: data?.top?.lifetime_credits_total ?? "0.00",
        lifetime_credits_count: data?.top?.lifetime_credits_count ?? 0,
        lifetime_debits_total: data?.top?.lifetime_debits_total ?? "0.00",
        lifetime_debits_count: data?.top?.lifetime_debits_count ?? 0,
        lifetime_self_count: data?.top?.lifetime_self_count ?? 0,
        lifetime_self_debits_count: data?.top?.lifetime_self_debits_count ?? 0,
        lifetime_self_debits_total: data?.top?.lifetime_self_debits_total ?? "0.00",
        lifetime_self_credits_total: data?.top?.lifetime_self_credits_total ?? "0.00",
        lifetime_self_credits_count: data?.top?.lifetime_self_credits_count ?? 0,
      });

      setAllTransactions(Array.isArray(data?.all_transactions) ? data.all_transactions : []);
      setMainWallet(Array.isArray(data?.main_wallet) ? data.main_wallet : (Array.isArray(data?.recent) ? data.recent : []));
      setIncoming(Array.isArray(data?.incoming) ? data.incoming : []);
      setSelfAccount(Array.isArray(data?.self_account) ? data.self_account : []);
      setCashback(Array.isArray(data?.cashback) ? data.cashback : []);
      setRedeem(Array.isArray(data?.redeem) ? data.redeem : []);

      // Load dynamic admin-configured platform taxes
      try {
        const taxRes = await API.get("/business/platform-taxes/");
        if (taxRes?.data) {
          setTaxConfig({
            p2p_package_tax_percent: Number(taxRes.data.p2p_package_tax_percent ?? 7),
            self_package_tax_percent: Number(taxRes.data.self_package_tax_percent ?? 7),
            p2p_coupon_tax_percent: Number(taxRes.data.p2p_coupon_tax_percent ?? 7),
            withdrawal_tax_percent: Number(taxRes.data.withdrawal_tax_percent ?? 10),
          });
        }
      } catch (err) {
        console.error("Failed to load platform taxes:", err);
      }
    } catch (e) {
      setErr("Failed to load history.");
    } finally {
      setLoading(false);
    }
  }, [termFilter, sourceFilter, flowFilter, datePreset, customStartDate, customEndDate, searchQuery, currentUser?.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchHistory();
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchHistory]);

  // Platform Tax Configuration
  const [taxConfig, setTaxConfig] = useState({
    p2p_package_tax_percent: 7,
    self_package_tax_percent: 7,
    p2p_coupon_tax_percent: 7,
    withdrawal_tax_percent: 10,
  });

  // Pockets Transfer State (Main Wallet -> P2P Coupon Pocket / Withdrawal Pocket)
  const [pocketType, setPocketType] = useState("withdrawal"); // "withdrawal" or "coupon"
  const [pocketAmount, setPocketAmount] = useState("");
  const [pocketOtpSent, setPocketOtpSent] = useState(false);
  const [pocketOtp, setPocketOtp] = useState("");
  const [pocketBusy, setPocketBusy] = useState(false);
  const [pocketError, setPocketError] = useState("");
  const [pocketSuccess, setPocketSuccess] = useState("");
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(600);
  const [resendCooldown, setResendCooldown] = useState(60);

  useEffect(() => {
    let timer;
    if (pocketOtpSent) {
      setOtpSecondsLeft(600);
      setResendCooldown(60);
      timer = setInterval(() => {
        setOtpSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
        setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [pocketOtpSent]);

  const pocketTaxPercent = Number(taxConfig.withdrawal_tax_percent || 10);
  const pocketGross = Number(pocketAmount || 0);
  const pocketTax = pocketGross > 0 ? Number(((pocketGross * pocketTaxPercent) / 100).toFixed(2)) : 0;
  const pocketNet = pocketGross > 0 ? Number((pocketGross - pocketTax).toFixed(2)) : 0;

  const handlePocketTransfer = async () => {
    if (pocketGross < 100) {
      setPocketError("Minimum transfer amount is ₹100.");
      return;
    }
    const avail = Number(top.main_income_balance || 0);
    if (pocketGross > avail) {
      setPocketError(`Insufficient balance. Available Main Wallet balance is ₹${fmtAmount(avail)}.`);
      return;
    }
    try {
      setPocketBusy(true);
      setPocketError("");
      setPocketSuccess("");

      await API.post("/accounts/wallet/transfer/request-otp/", {
        transfer_type: pocketType,
        amount: pocketGross,
      });

      setPocketOtpSent(true);
      setPocketSuccess(
        `OTP sent to your registered email to confirm transfer of ₹${fmtAmount(pocketGross)} to Withdrawable Wallet.`
      );
    } catch (err) {
      setPocketError(err?.response?.data?.detail || "Failed to process transfer OTP.");
    } finally {
      setPocketBusy(false);
    }
  };

  const handleConfirmPocketOtp = async () => {
    if (!pocketOtp.trim()) {
      setPocketError("Please enter the 6-digit OTP.");
      return;
    }
    try {
      setPocketBusy(true);
      setPocketError("");
      setPocketSuccess("");

      await API.post("/accounts/wallet/transfer/confirm-otp/", {
        transfer_type: pocketType,
        otp: pocketOtp.trim(),
      });

      setPocketSuccess(
        `Successfully transferred ₹${fmtAmount(pocketNet)} into Withdrawable Wallet (${pocketTaxPercent}% TDS: ₹${fmtAmount(pocketTax)})!`
      );
      setPocketAmount("");
      setPocketOtp("");
      setPocketOtpSent(false);
      fetchHistory();
    } catch (err) {
      setPocketError(err?.response?.data?.detail || "Failed to confirm transfer.");
    } finally {
      setPocketBusy(false);
    }
  };

  const p2pTaxPercent = Number(taxConfig.p2p_package_tax_percent || 7);
  const selfTaxPercent = Number(taxConfig.self_package_tax_percent ?? 7);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (termFilter && termFilter !== "ALL") count++;
    if (datePreset !== "all" || customStartDate || customEndDate) count++;
    if (flowFilter !== "ALL") count++;
    if (sourceFilter !== "ALL") count++;
    if (searchQuery.trim()) count++;
    return count;
  }, [termFilter, datePreset, customStartDate, customEndDate, flowFilter, sourceFilter, searchQuery]);

  const handleResetFilters = () => {
    setTermFilter("ALL");
    setDatePreset("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setFlowFilter("ALL");
    setSourceFilter("ALL");
    setSearchQuery("");
  };

  // 100% Database SQL Sourced: All filtering & calculations computed exclusively by PostgreSQL on the backend
  const filteredTransactions = Array.isArray(allTransactions) ? allTransactions : [];

  const sections = useMemo(() => groupByMonth(filteredTransactions), [filteredTransactions]);

  const ledgerSummary = useMemo(() => {
    return {
      totalCredits: Number(top?.all_earnings_total ?? 0),
      creditCount: Number(top?.all_earnings_count ?? top?.lifetime_credits_count ?? 0),
      totalDebits: Number(top?.lifetime_debits_total ?? 0),
      debitCount: Number(top?.lifetime_debits_count ?? 0),
      mainCredits: Number(top?.main_income_balance ?? 0),
      mainCount: Number(top?.lifetime_credits_count ?? 0),
      selfCredits: Number(top?.lifetime_self_credits_total ?? 0),
      selfDebits: Number(top?.lifetime_self_debits_total ?? 0),
      totalSelf: Number(top?.self_account_balance ?? 0),
      selfCount: Number(top?.lifetime_self_count ?? 0),
    };
  }, [top]);

  const categoryStats = useMemo(() => {
    const defaultStats = {
      ALL: { count: 0, total: 0 },
      FIVE_LAYER: { count: 0, total: 0 },
      THREE_LAYER: { count: 0, total: 0 },
      E_EDU_LAYER: { count: 0, total: 0 },
      E_EDU_DIRECT: { count: 0, total: 0 },
      E_EDU: { count: 0, total: 0 },
      SAVE: { count: 0, total: 0 },
      SSV_LAYER: { count: 0, total: 0 },
      SSV_BONUS: { count: 0, total: 0 },
      QR_SCANNER: { count: 0, total: 0 },
      TRIZONE: { count: 0, total: 0 },
      ROYALTY: { count: 0, total: 0 },
    };
    if (dbCategoryStats && typeof dbCategoryStats === "object") {
      return {
        ...defaultStats,
        ...dbCategoryStats,
      };
    }
    return defaultStats;
  }, [dbCategoryStats]);

  return (
    <Box
      sx={{
        bgcolor: "#F4F7FC",
        minHeight: "100dvh",
        position: "relative",
        pb: { xs: 12, sm: 14 },
      }}
    >
      <PremiumScreenHeader
        title="Wallet & History"
        user={currentUser}
        notificationCount={1}
        onBack={() => navigate(-1)}
        onNotifications={() => {
          try {
            window.dispatchEvent(new CustomEvent("trikonekt:open-consumer-sidebar"));
          } catch (_) {}
        }}
        onSecondary={() => navigate("/user/spp-gift-cards")}
        hasBackdrop={true}
      />

      <Box
        sx={{
          maxWidth: 600,
          mx: "auto",
          px: { xs: 2, sm: 2.5 },
          pt: 1,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* 1. EMERALD GRADIENT MAIN WALLET HERO CARD (Consumer Clean View - No Earning Limit) */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.2, sm: 2.5 },
            borderRadius: "24px",
            mb: 1.8,
            position: "relative",
            overflow: "hidden",
            background: "linear-gradient(135deg, #064E3B 0%, #065F46 50%, #022C22 100%)",
            color: "#FFFFFF",
            boxShadow: "0 14px 34px -8px rgba(6, 78, 59, 0.45)",
            border: "1px solid rgba(255, 255, 255, 0.16)",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "14px",
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(12px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(255, 255, 255, 0.25)",
                  flexShrink: 0,
                }}
              >
                <AccountBalanceWalletIcon sx={{ fontSize: 26, color: "#FFFFFF" }} />
              </Box>
              <Box>
                <Typography sx={{ color: "rgba(255, 255, 255, 0.8)", fontWeight: 700, fontSize: 12, letterSpacing: 0.3 }}>
                  Main Wallet Balance
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 26, sm: 30 },
                    fontWeight: 900,
                    lineHeight: 1.1,
                    mt: 0.2,
                    color: "#FFFFFF",
                    letterSpacing: "-0.5px",
                  }}
                >
                  ₹ {fmtAmount(Number(top.main_income_balance || 0) > 0 ? top.main_income_balance : 0)}
                </Typography>
              </Box>
            </Stack>

            <Chip
              label={`Term ${currentTerm} Active`}
              size="small"
              onClick={() => navigate("/rank-upgrade")}
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.18)",
                color: "#FFFFFF",
                fontWeight: 800,
                fontSize: 11,
                cursor: "pointer",
                border: "1px solid rgba(255, 255, 255, 0.35)",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.3)" },
              }}
            />
          </Stack>

          {/* Self Account Pocket Balance Pill */}
          <Box
            onClick={() => setSourceFilter("SAVE")}
            sx={{
              mt: 1.8,
              p: 1.2,
              borderRadius: "14px",
              bgcolor: "rgba(255, 255, 255, 0.12)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              transition: "all 0.15s ease",
              "&:hover": { bgcolor: "rgba(255, 255, 255, 0.18)" },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "10px",
                  bgcolor: "rgba(254, 243, 199, 0.25)",
                  color: "#FDE68A",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(253, 230, 138, 0.4)",
                }}
              >
                <SavingsIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11.5, color: "rgba(255, 255, 255, 0.9)", fontWeight: 700 }}>
                  Self Account Pocket
                </Typography>
              </Box>
            </Stack>

            <Typography sx={{ fontSize: 15, fontWeight: 900, color: "#FDE68A", letterSpacing: "-0.01em" }}>
              ₹ {fmtAmount(ledgerSummary.totalSelf || 0)}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ mt: 1.8 }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<ArrowUpwardIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                setDrawerMode("pockets");
                setPocketType("withdrawal");
                setActionDrawerOpen(true);
              }}
              sx={{
                flex: 1,
                py: 0.7,
                minHeight: 36,
                fontSize: { xs: 11, sm: 12 },
                fontWeight: 800,
                textTransform: "none",
                borderRadius: "12px",
                bgcolor: "#10B981",
                boxShadow: "0 2px 8px rgba(16, 185, 129, 0.35)",
                color: "#FFFFFF",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: "#059669" },
              }}
            >
              Withdraw Wallet
            </Button>

            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setDrawerMode("menu");
                setActionDrawerOpen(true);
              }}
              sx={{
                minWidth: 42,
                height: 36,
                p: 0,
                borderRadius: "12px",
                bgcolor: "rgba(255, 255, 255, 0.16)",
                backdropFilter: "blur(8px)",
                color: "#FFFFFF",
                border: "1px solid rgba(255, 255, 255, 0.25)",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.25)" },
              }}
            >
              <MoreHorizRoundedIcon sx={{ fontSize: 20 }} />
            </Button>
          </Stack>
        </Paper>

      {/* Bottom Sheet Drawer for Main Wallet Actions & In-Drawer P2P Transfer */}
      <Drawer
        anchor="bottom"
        open={actionDrawerOpen}
        onClose={() => setActionDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 28,
            borderTopRightRadius: 28,
            p: { xs: 2, sm: 2.5 },
            pb: { xs: 4, sm: 3.5 },
            maxWidth: 600,
            mx: "auto",
            bgcolor: "#FFFFFF",
            boxShadow: "0 -12px 40px rgba(15,23,42,0.18)",
            maxHeight: "85vh",
            overflowY: "auto",
          },
        }}
      >
        {/* Drag pill indicator */}
        <Box sx={{ width: 40, height: 4, bgcolor: "#CBD5E1", borderRadius: 2, mx: "auto", mb: 2 }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 900, color: "#0F172A" }}>
              Move to Pockets & Wallet Actions
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#059669", fontWeight: 700 }}>
              Available Balance: ₹ {fmtAmount(top.main_income_balance)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setActionDrawerOpen(false)}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        {/* Action Mode Toggle Pills */}
        <Stack direction="row" spacing={0.8} sx={{ mb: 2 }}>
          <Button
            size="small"
            variant={drawerMode === "withdrawal" || drawerMode === "pockets" || drawerMode === "coupon" ? "contained" : "outlined"}
            onClick={() => { setDrawerMode("pockets"); }}
            sx={{
              flex: 1.2,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              fontSize: 12,
              bgcolor: drawerMode === "withdrawal" || drawerMode === "pockets" || drawerMode === "coupon" ? "#0256B4" : "transparent",
              borderColor: "#0256B4",
              color: drawerMode === "withdrawal" || drawerMode === "pockets" || drawerMode === "coupon" ? "#fff" : "#0256B4",
            }}
          >
            Transfer from Wallet
          </Button>
          {!isGrowthProduction() && (
            <Button
              size="small"
              variant={drawerMode === "p2p" ? "contained" : "outlined"}
              onClick={() => setDrawerMode("p2p")}
              sx={{
                flex: 1,
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 800,
                fontSize: 12,
                bgcolor: drawerMode === "p2p" ? "#0066E6" : "transparent",
                borderColor: "#0066E6",
                color: drawerMode === "p2p" ? "#fff" : "#0066E6",
              }}
            >
              Gift Voucher
            </Button>
          )}
          <Button
            size="small"
            variant={drawerMode === "menu" ? "contained" : "outlined"}
            onClick={() => setDrawerMode("menu")}
            sx={{
              flex: 0.8,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              fontSize: 12,
              bgcolor: drawerMode === "menu" ? "#0F172A" : "transparent",
              borderColor: "#CBD5E1",
              color: drawerMode === "menu" ? "#fff" : "#475569",
            }}
          >
            Quick Links
          </Button>
        </Stack>

        {/* Unified Transfer from Main Wallet Sheet */}
        {(drawerMode === "withdrawal" || drawerMode === "pockets" || drawerMode === "coupon") && (
          <Box sx={{ mb: 2 }}>
            {isGrowthProduction() ? (
              <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 13, my: 1 }}>
                Transfers from Main Wallet to Withdrawal or Coupon Pockets are currently paused on asiyapp.com.
              </Alert>
            ) : (
              <>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0F172A", mb: 1 }}>
                  Select Destination
                </Typography>

            {/* Destination Selection Cards */}
            <Grid container spacing={1.2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  onClick={() => setPocketType("withdrawal")}
                  sx={{
                    p: 1.5,
                    borderRadius: "14px",
                    cursor: "pointer",
                    border: "2px solid #059669",
                    bgcolor: "#ECFDF5",
                    transition: "all 140ms ease",
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#059669" }}>
                    Withdrawable Wallet
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#64748B", mt: 0.3 }}>
                    Bank payout • {pocketTaxPercent}% TDS (Direct bank transfer)
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {!isGrowthProduction() && (
              <Paper
                elevation={0}
                onClick={() => setDrawerMode("p2p")}
                sx={{
                  p: 1.25,
                  px: 1.5,
                  borderRadius: "14px",
                  cursor: "pointer",
                  border: "1.5px dashed #0066E6",
                  bgcolor: "#EFF6FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                  transition: "all 140ms ease",
                  "&:hover": { bgcolor: "#DBEAFE" },
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#0066E6" }}>
                    Looking to Send a Gift Voucher?
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#475569", mt: 0.2 }}>
                    Send full 100% face-value package vouchers directly to members ({p2pTaxPercent}% Fee)
                  </Typography>
                </Box>
                <Button size="small" variant="contained" sx={{ textTransform: "none", borderRadius: 999, fontSize: 11, px: 1.5, py: 0.4, bgcolor: "#0066E6" }}>
                  Send Voucher →
                </Button>
              </Paper>
            )}

            {Number(top.main_income_balance) < 100 && (
              <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2, fontSize: 12.5 }}>
                Minimum ₹100 in Main Wallet is required to transfer funds.
              </Alert>
            )}

            {pocketError && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, fontSize: 12.5 }}>{pocketError}</Alert>}
            {pocketSuccess && <Alert severity="success" sx={{ mb: 1.5, borderRadius: 2, fontSize: 12.5 }}>{pocketSuccess}</Alert>}

            {!pocketOtpSent ? (
              <Stack spacing={1.5}>
                <Box>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#334155", mb: 0.6 }}>
                    Enter Amount
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Min ₹100"
                    type="number"
                    value={pocketAmount}
                    onChange={(e) => {
                      setPocketAmount(e.target.value);
                      setPocketError("");
                    }}
                    disabled={Number(top.main_income_balance) < 100}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      endAdornment: (
                        <Button
                          size="small"
                          onClick={() => {
                            setPocketAmount(String(fmtAmount(top.main_income_balance)));
                            setPocketError("");
                          }}
                          sx={{ fontWeight: 800, fontSize: 11.5, minWidth: 44, color: "#0256B4" }}
                        >
                          MAX
                        </Button>
                      ),
                    }}
                    sx={{ bgcolor: "#FFFFFF", borderRadius: "12px" }}
                  />
                  <Typography sx={{ fontSize: 11.5, color: "#64748B", mt: 0.5 }}>
                    Available Balance: ₹ {fmtAmount(top.main_income_balance)}
                  </Typography>
                </Box>

                {pocketGross > 0 && (
                  <Box sx={{ p: 1.4, bgcolor: "#F8FAFC", borderRadius: "14px", border: "1px solid #E2E8F0" }}>
                    <Stack spacing={0.5}>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#64748B" }}>Gross Amount:</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0F172A" }}>₹ {pocketGross.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 12, color: "#DC2626" }}>{pocketTaxPercent}% Tax / Platform Fee:</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#DC2626" }}>- ₹ {pocketTax.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", pt: 0.5, borderTop: "1px dashed #CBD5E1" }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#059669" }}>Net Credited to Destination:</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#059669" }}>₹ {pocketNet.toFixed(2)}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  disabled={pocketBusy || pocketGross < 100 || pocketGross > Number(top.main_income_balance || 0) || Number(top.main_income_balance) < 100}
                  onClick={handlePocketTransfer}
                  sx={{
                    py: 1.25,
                    borderRadius: "14px",
                    fontWeight: 800,
                    fontSize: 13.5,
                    textTransform: "none",
                    background: "linear-gradient(135deg, #0256B4 0%, #0066E6 100%)",
                    "&:hover": { background: "linear-gradient(135deg, #0047AB 0%, #0256B4 100%)" },
                  }}
                >
                  {pocketBusy ? "Sending OTP..." : "Continue →"}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 0.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
                    Enter 6-Digit Email OTP
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                      color: otpSecondsLeft === 0 ? "#DC2626" : otpSecondsLeft < 120 ? "#D97706" : "#059669",
                    }}
                  >
                    {otpSecondsLeft === 0
                      ? "Expired"
                      : `Expires in ${Math.floor(otpSecondsLeft / 60)}:${String(otpSecondsLeft % 60).padStart(2, "0")}`}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. 123456"
                  value={pocketOtp}
                  disabled={otpSecondsLeft === 0}
                  onChange={(e) => {
                    setPocketOtp(e.target.value);
                    setPocketError("");
                  }}
                  sx={{ bgcolor: "#fff", borderRadius: 2 }}
                />

                {otpSecondsLeft === 0 && (
                  <Alert severity="warning" sx={{ py: 0.5, borderRadius: 2, fontSize: 12 }}>
                    This OTP has expired. Please tap <strong>Resend OTP</strong> to get a new code.
                  </Alert>
                )}

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 0.5 }}>
                  <Typography sx={{ fontSize: 11.5, color: "#64748B" }}>
                    Didn't receive email? Check spam or resend:
                  </Typography>
                  <Button
                    size="small"
                    disabled={pocketBusy || resendCooldown > 0}
                    onClick={() => {
                      setPocketOtp("");
                      handlePocketTransfer();
                    }}
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                      textTransform: "none",
                      color: resendCooldown > 0 ? "#94A3B8" : "#0256B4",
                      p: 0,
                      minWidth: "auto",
                    }}
                  >
                    {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : "Resend OTP"}
                  </Button>
                </Box>

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setPocketOtpSent(false);
                      setPocketOtp("");
                    }}
                    sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={pocketBusy || !pocketOtp.trim() || otpSecondsLeft === 0}
                    onClick={handleConfirmPocketOtp}
                    sx={{
                      borderRadius: "12px",
                      fontWeight: 800,
                      textTransform: "none",
                      bgcolor: "#059669",
                      "&:hover": { bgcolor: "#047857" },
                    }}
                  >
                    {pocketBusy ? "Verifying..." : "Confirm & Credit Pocket"}
                  </Button>
                </Stack>
              </Stack>
            )}
              </>
            )}
          </Box>
        )}

        {/* Drawer Mode 1: P2P Transfer */}
        {drawerMode === "p2p" && (
          <P2PTransferPanel
            p2pTaxPercent={p2pTaxPercent}
            selfTaxPercent={selfTaxPercent}
            availableBalance={Number(top.main_income_balance || top.withdrawable_balance || 0)}
            onSuccess={() => fetchHistory()}
          />
        )}

        {/* Quick External & Portal Actions */}
        <Stack spacing={1.2}>
          {/* E-edu Academy */}
          <Paper
            elevation={0}
            onClick={() => {
              setActionDrawerOpen(false);
              navigate("/user/rank-upgrade");
            }}
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              border: "1px solid #E2E8F0",
              bgcolor: "#F8FAFC",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "all 0.15s ease",
              "&:hover": { bgcolor: "#F5F3FF", borderColor: "#7C3AED" },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#F5F3FF", color: "#7C3AED", width: 40, height: 40 }}>
                <SchoolRoundedIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#0F172A" }}>
                  E-edu Agent Academy
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#64748B" }}>
                  Digital course learning & rank certification on asiyapp.com
                </Typography>
              </Box>
            </Stack>
            <ChevronRightIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
          </Paper>

          {/* Self Withdrawal */}
          <Paper
            elevation={0}
            onClick={() => {
              setActionDrawerOpen(false);
              navigate("/user/withdrawal");
            }}
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              border: "1px solid #E2E8F0",
              bgcolor: "#F8FAFC",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "all 0.15s ease",
              "&:hover": { bgcolor: "#ECFDF5", borderColor: "#059669" },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#ECFDF5", color: "#059669", width: 40, height: 40 }}>
                <AccountBalanceRoundedIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#0F172A" }}>
                  Self Withdrawal
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#64748B" }}>
                  Instant payout to your verified bank account
                </Typography>
              </Box>
            </Stack>
            <ChevronRightIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
          </Paper>
        </Stack>
      </Drawer>

      {/* 2. TOTAL CREDITS & TOTAL DEBITS KPI CARDS (Matching Image 2) */}
      <Grid container spacing={1.5} sx={{ mb: 1.8 }}>
        <Grid item xs={6}>
          <Paper
            elevation={0}
            onClick={() => setFlowFilter("CREDIT")}
            sx={{
              p: 1.8,
              borderRadius: "18px",
              bgcolor: "#FFFFFF",
              border: "1px solid",
              borderColor: flowFilter === "CREDIT" ? "#059669" : "#EEF2F6",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.03)",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: "#ECFDF5",
                  color: "#059669",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #A7F3D0",
                }}
              >
                <ArrowUpwardIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>
                Total Earnings
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 900, color: "#059669", letterSpacing: "-0.02em" }}>
              ₹ {fmtAmount(ledgerSummary.totalCredits)}
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", mt: 0.2 }}>
              {ledgerSummary.creditCount} transactions
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={6}>
          <Paper
            elevation={0}
            onClick={() => {
              setFlowFilter("DEBIT");
              setSourceFilter("SAVE");
            }}
            sx={{
              p: 1.8,
              borderRadius: "18px",
              bgcolor: "#FFFFFF",
              border: "1px solid",
              borderColor: (flowFilter === "DEBIT" && sourceFilter === "SAVE") ? "#DC2626" : "#EEF2F6",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.03)",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: "#FEF2F2",
                  color: "#DC2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #FECACA",
                }}
              >
                <ArrowDownwardIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>
                Self Block
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 900, color: "#DC2626", letterSpacing: "-0.02em" }}>
              ₹ {fmtAmount(ledgerSummary.selfDebits || 0)}
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", mt: 0.2 }}>
              {Math.floor(Number(ledgerSummary.selfDebits || 0) / 250)} IDs generated
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* 2.5 ACADEMIC TERM SELECTOR (Term 2 shown strictly only when purchased) */}
      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          overflowX: "auto",
          pb: 0.5,
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        <Chip
          icon={<SchoolRoundedIcon sx={{ fontSize: 16, color: termFilter === "ALL" ? "#FFF !important" : "#059669 !important" }} />}
          label={`All Terms (${categoryStats.ALL.count})`}
          onClick={() => setTermFilter("ALL")}
          sx={{
            flexShrink: 0,
            fontWeight: 800,
            fontSize: 11.5,
            height: 32,
            borderRadius: "999px",
            bgcolor: termFilter === "ALL" ? "#064E3B" : "#FFFFFF",
            color: termFilter === "ALL" ? "#FFFFFF" : "#064E3B",
            border: "1.5px solid",
            borderColor: termFilter === "ALL" ? "#064E3B" : "#A7F3D0",
            cursor: "pointer",
            boxShadow: "0 1px 4px rgba(6, 78, 59, 0.08)",
          }}
        />

        <Chip
          label={`Term 1 ${dbTermStats["1"] ? `(₹${fmtAmount(dbTermStats["1"].cycle_earned || dbTermStats["1"].total)})` : ""}`}
          onClick={() => setTermFilter("1")}
          sx={{
            flexShrink: 0,
            fontWeight: 800,
            fontSize: 11.5,
            height: 32,
            borderRadius: "999px",
            bgcolor: termFilter === "1" ? "#064E3B" : "#FFFFFF",
            color: termFilter === "1" ? "#FFFFFF" : "#334155",
            border: "1.5px solid",
            borderColor: termFilter === "1" ? "#064E3B" : "#CBD5E1",
            cursor: "pointer",
            boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)",
          }}
        />

        {purchasedTerms.includes(2) && (
          <Chip
            label={`Term 2 (Active) ${dbTermStats["2"] ? `(₹${fmtAmount(dbTermStats["2"].cycle_earned || dbTermStats["2"].total)})` : ""}`}
            onClick={() => setTermFilter("2")}
            sx={{
              flexShrink: 0,
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: termFilter === "2" ? "#064E3B" : "#FFFFFF",
              color: termFilter === "2" ? "#FFFFFF" : "#2563EB",
              border: "1.5px solid",
              borderColor: termFilter === "2" ? "#064E3B" : "#93C5FD",
              cursor: "pointer",
              boxShadow: "0 1px 4px rgba(37, 99, 235, 0.08)",
            }}
          />
        )}

        {purchasedTerms.includes(3) && (
          <Chip
            label={`Term 3 ${dbTermStats["3"] ? `(₹${fmtAmount(dbTermStats["3"].cycle_earned || dbTermStats["3"].total)})` : ""}`}
            onClick={() => setTermFilter("3")}
            sx={{
              flexShrink: 0,
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: termFilter === "3" ? "#064E3B" : "#FFFFFF",
              color: termFilter === "3" ? "#FFFFFF" : "#334155",
              border: "1.5px solid",
              borderColor: termFilter === "3" ? "#064E3B" : "#E2E8F0",
              cursor: "pointer",
            }}
          />
        )}
      </Box>

      {/* 3. SEARCH + HORIZONTAL FILTER BAR (Image 2 exact styling) */}
      <Stack spacing={1.2} sx={{ mb: 1.8 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, phone, reference..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: "#065F46", fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ p: 0.5 }}>
                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                borderRadius: "24px",
                bgcolor: "#FFFFFF",
                fontSize: 13,
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
                "& fieldset": { borderColor: "#E2E8F0" },
                "&:hover fieldset": { borderColor: "#CBD5E1" },
                "&.Mui-focused fieldset": { borderColor: "#059669" },
              },
            }}
          />

          <Button
            variant="outlined"
            onClick={() => setFilterDrawerOpen(true)}
            startIcon={
              <Badge
                badgeContent={activeFilterCount}
                color="error"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: 10,
                    height: 16,
                    minWidth: 16,
                    top: -2,
                    right: -2,
                  },
                }}
              >
                <TuneRoundedIcon sx={{ fontSize: 18, color: activeFilterCount > 0 ? "#059669" : "#475569" }} />
              </Badge>
            }
            sx={{
              height: 40,
              minWidth: 96,
              borderRadius: "24px",
              textTransform: "none",
              fontWeight: 800,
              fontSize: 12.5,
              bgcolor: activeFilterCount > 0 ? "#ECFDF5" : "#FFFFFF",
              borderColor: activeFilterCount > 0 ? "#059669" : "#E2E8F0",
              color: activeFilterCount > 0 ? "#059669" : "#334155",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
              flexShrink: 0,
              "&:hover": { borderColor: "#059669", bgcolor: "#ECFDF5" },
            }}
          >
            Filters
          </Button>
        </Stack>

        {/* 6 Horizontal Category Filter Chips Matching Image 2 */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            overflowX: "auto",
            pb: 0.6,
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {/* Chip 1: All */}
          <Chip
            label={`All (${categoryStats.ALL.count}) ₹${fmtAmount(categoryStats.ALL.total)}`}
            onClick={() => setSourceFilter("ALL")}
            sx={{
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: sourceFilter === "ALL" ? "#0F172A" : "#FFFFFF",
              color: sourceFilter === "ALL" ? "#FFFFFF" : "#475569",
              border: "1.5px solid",
              borderColor: sourceFilter === "ALL" ? "#0F172A" : "#E2E8F0",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          />

          {/* Chip 2: Self Account (25%) */}
          <Chip
            icon={<SavingsIcon sx={{ fontSize: "16px !important", color: (sourceFilter === "SAVE" || sourceFilter === "SELF_ACCOUNT") ? "#FFFFFF !important" : "#D97706 !important" }} />}
            label={`Self Account (${categoryStats.SAVE.count}) ₹${fmtAmount(categoryStats.SAVE.total)}`}
            onClick={() => setSourceFilter("SAVE")}
            sx={{
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: (sourceFilter === "SAVE" || sourceFilter === "SELF_ACCOUNT") ? "#D97706" : "#FFFFFF",
              color: (sourceFilter === "SAVE" || sourceFilter === "SELF_ACCOUNT") ? "#FFFFFF" : "#92400E",
              border: "1.5px solid",
              borderColor: (sourceFilter === "SAVE" || sourceFilter === "SELF_ACCOUNT") ? "#D97706" : "#FDE68A",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          />

          {/* Chip 3: 5-Matrix Layer */}
          <Chip
            icon={<LayersRoundedIcon sx={{ fontSize: "16px !important", color: sourceFilter === "FIVE_LAYER" ? "#FFFFFF !important" : "#2563EB !important" }} />}
            label={`5 Layer (${categoryStats.FIVE_LAYER.count}) ₹${fmtAmount(categoryStats.FIVE_LAYER.total)}`}
            onClick={() => setSourceFilter("FIVE_LAYER")}
            sx={{
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: sourceFilter === "FIVE_LAYER" ? "#2563EB" : "#FFFFFF",
              color: sourceFilter === "FIVE_LAYER" ? "#FFFFFF" : "#1D4ED8",
              border: "1.5px solid",
              borderColor: sourceFilter === "FIVE_LAYER" ? "#2563EB" : "#BFDBFE",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          />

          {/* Chip 3: 3-Matrix Layer */}
          <Chip
            icon={<AccountTreeRoundedIcon sx={{ fontSize: "16px !important", color: sourceFilter === "THREE_LAYER" ? "#FFFFFF !important" : "#0D9488 !important" }} />}
            label={`3 Layer (${categoryStats.THREE_LAYER.count}) ₹${fmtAmount(categoryStats.THREE_LAYER.total)}`}
            onClick={() => setSourceFilter("THREE_LAYER")}
            sx={{
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: sourceFilter === "THREE_LAYER" ? "#0D9488" : "#FFFFFF",
              color: sourceFilter === "THREE_LAYER" ? "#FFFFFF" : "#0F766E",
              border: "1.5px solid",
              borderColor: sourceFilter === "THREE_LAYER" ? "#0D9488" : "#99F6E4",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          />

          {/* Chip 4: E-Edu Layer */}
          <Chip
            icon={<SchoolRoundedIcon sx={{ fontSize: "16px !important", color: sourceFilter === "E_EDU_LAYER" ? "#FFFFFF !important" : "#7C3AED !important" }} />}
            label={`E-Edu Layer (${categoryStats.E_EDU_LAYER.count}) ₹${fmtAmount(categoryStats.E_EDU_LAYER.total)}`}
            onClick={() => setSourceFilter("E_EDU_LAYER")}
            sx={{
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: sourceFilter === "E_EDU_LAYER" ? "#7C3AED" : "#FFFFFF",
              color: sourceFilter === "E_EDU_LAYER" ? "#FFFFFF" : "#6D28D9",
              border: "1.5px solid",
              borderColor: sourceFilter === "E_EDU_LAYER" ? "#7C3AED" : "#DDD6FE",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          />

          {/* Chip 5: E-Edu Direct Referral */}
          <Chip
            icon={<SchoolRoundedIcon sx={{ fontSize: "16px !important", color: sourceFilter === "E_EDU_DIRECT" ? "#FFFFFF !important" : "#0284C7 !important" }} />}
            label={`E-Edu Direct (${categoryStats.E_EDU_DIRECT.count}) ₹${fmtAmount(categoryStats.E_EDU_DIRECT.total)}`}
            onClick={() => setSourceFilter("E_EDU_DIRECT")}
            sx={{
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: sourceFilter === "E_EDU_DIRECT" ? "#0284C7" : "#FFFFFF",
              color: sourceFilter === "E_EDU_DIRECT" ? "#FFFFFF" : "#0369A1",
              border: "1.5px solid",
              borderColor: sourceFilter === "E_EDU_DIRECT" ? "#0284C7" : "#BAE6FD",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          />

          {/* Chip 6: E-Edu All */}
          <Chip
            icon={<SchoolRoundedIcon sx={{ fontSize: "16px !important", color: sourceFilter === "E_EDU" ? "#FFFFFF !important" : "#2563EB !important" }} />}
            label={`E-Edu All (${categoryStats.E_EDU.count}) ₹${fmtAmount(categoryStats.E_EDU.total)}`}
            onClick={() => setSourceFilter("E_EDU")}
            sx={{
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: sourceFilter === "E_EDU" ? "#2563EB" : "#FFFFFF",
              color: sourceFilter === "E_EDU" ? "#FFFFFF" : "#1E40AF",
              border: "1.5px solid",
              borderColor: sourceFilter === "E_EDU" ? "#2563EB" : "#BFDBFE",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          />


          {/* Chip 4: SSV Layer */}
          <Chip
            icon={<LayersRoundedIcon sx={{ fontSize: "16px !important", color: sourceFilter === "SSV_LAYER" ? "#FFFFFF !important" : "#7C3AED !important" }} />}
            label={`SSV Layer (${categoryStats.SSV_LAYER.count}) ₹${fmtAmount(categoryStats.SSV_LAYER.total)}`}
            onClick={() => setSourceFilter("SSV_LAYER")}
            sx={{
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: sourceFilter === "SSV_LAYER" ? "#7C3AED" : "#FFFFFF",
              color: sourceFilter === "SSV_LAYER" ? "#FFFFFF" : "#5B21B6",
              border: "1.5px solid",
              borderColor: sourceFilter === "SSV_LAYER" ? "#7C3AED" : "#DDD6FE",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          />

          {/* Chip 5: SSV Bonus */}
          <Chip
            icon={<StarsRoundedIcon sx={{ fontSize: "16px !important", color: sourceFilter === "SSV_BONUS" ? "#FFFFFF !important" : "#EA580C !important" }} />}
            label={`SSV Bonus (${categoryStats.SSV_BONUS.count}) ₹${fmtAmount(categoryStats.SSV_BONUS.total)}`}
            onClick={() => setSourceFilter("SSV_BONUS")}
            sx={{
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: sourceFilter === "SSV_BONUS" ? "#EA580C" : "#FFFFFF",
              color: sourceFilter === "SSV_BONUS" ? "#FFFFFF" : "#9A3412",
              border: "1.5px solid",
              borderColor: sourceFilter === "SSV_BONUS" ? "#EA580C" : "#FFEDD5",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          />

          {/* Chip 6: QR Scanner */}
          <Chip
            icon={<QrCode2RoundedIcon sx={{ fontSize: "16px !important", color: sourceFilter === "QR_SCANNER" ? "#FFFFFF !important" : "#059669 !important" }} />}
            label={`QR Scanner (${categoryStats.QR_SCANNER.count}) ₹${fmtAmount(categoryStats.QR_SCANNER.total)}`}
            onClick={() => setSourceFilter("QR_SCANNER")}
            sx={{
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: sourceFilter === "QR_SCANNER" ? "#059669" : "#FFFFFF",
              color: sourceFilter === "QR_SCANNER" ? "#FFFFFF" : "#065F46",
              border: "1.5px solid",
              borderColor: sourceFilter === "QR_SCANNER" ? "#059669" : "#A7F3D0",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          />

          {/* Chip 7: Trizone Shopping */}
          <Chip
            icon={<ShoppingCartRoundedIcon sx={{ fontSize: "16px !important", color: sourceFilter === "TRIZONE" ? "#FFFFFF !important" : "#0284C7 !important" }} />}
            label={`Trizone Shopping (${categoryStats.TRIZONE.count}) ₹${fmtAmount(categoryStats.TRIZONE.total)}`}
            onClick={() => setSourceFilter("TRIZONE")}
            sx={{
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: sourceFilter === "TRIZONE" ? "#0284C7" : "#FFFFFF",
              color: sourceFilter === "TRIZONE" ? "#FFFFFF" : "#075985",
              border: "1.5px solid",
              borderColor: sourceFilter === "TRIZONE" ? "#0284C7" : "#BAE6FD",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          />

          {/* Chip 8: Royalty */}
          <Chip
            icon={<WorkspacePremiumRoundedIcon sx={{ fontSize: "16px !important", color: sourceFilter === "ROYALTY" ? "#FFFFFF !important" : "#9333EA !important" }} />}
            label={`Royalty (${categoryStats.ROYALTY.count}) ₹${fmtAmount(categoryStats.ROYALTY.total)}`}
            onClick={() => setSourceFilter("ROYALTY")}
            sx={{
              fontWeight: 800,
              fontSize: 11.5,
              height: 32,
              borderRadius: "999px",
              bgcolor: sourceFilter === "ROYALTY" ? "#9333EA" : "#FFFFFF",
              color: sourceFilter === "ROYALTY" ? "#FFFFFF" : "#7E22CE",
              border: "1.5px solid",
              borderColor: sourceFilter === "ROYALTY" ? "#9333EA" : "#E9D5FF",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          />
        </Box>

        {/* Dynamic Category Summary Banner Matching Image 2 */}
        {sourceFilter === "FIVE_LAYER" && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#EFF6FF", border: "1px solid #BFDBFE" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#DBEAFE", color: "#1D4ED8", width: 38, height: 38 }}>
                <LayersRoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#1E3A8A" }}>
                  5-Matrix Layer AutoPool
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#1D4ED8", fontWeight: 700 }}>
                  Total Transactions: {categoryStats.FIVE_LAYER.count} • Total Amount Received: ₹ {fmtAmount(categoryStats.FIVE_LAYER.total)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#1E40AF", opacity: 0.85, mt: 0.2 }}>
                  5-Matrix level-based autopool income distributed across 5-team tree placements.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {sourceFilter === "THREE_LAYER" && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#F0FDFA", border: "1px solid #99F6E4" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#CCFBF1", color: "#0F766E", width: 38, height: 38 }}>
                <AccountTreeRoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#115E59" }}>
                  3-Matrix Layer AutoPool
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#0F766E", fontWeight: 700 }}>
                  Total Transactions: {categoryStats.THREE_LAYER.count} • Total Amount Received: ₹ {fmtAmount(categoryStats.THREE_LAYER.total)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#134E4A", opacity: 0.85, mt: 0.2 }}>
                  3-Matrix global BFS autopool earnings filled left-to-right from platform purchases.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {sourceFilter === "E_EDU_LAYER" && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#FAF5FF", border: "1px solid #E9D5FF" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#F3E8FF", color: "#7E22CE", width: 38, height: 38 }}>
                <SchoolRoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#581C87" }}>
                  E-Edu Layer Commissions
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#7E22CE", fontWeight: 700 }}>
                  Total Transactions: {categoryStats.E_EDU_LAYER.count} • Total Amount Received: ₹ {fmtAmount(categoryStats.E_EDU_LAYER.total)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#6B21A8", opacity: 0.85, mt: 0.2 }}>
                  Generation-matching layer bonus distributed across 10-rank E-Education upgrades.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {sourceFilter === "E_EDU_DIRECT" && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#F0F9FF", border: "1px solid #BAE6FD" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#E0F2FE", color: "#0284C7", width: 38, height: 38 }}>
                <SchoolRoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#0C4A6E" }}>
                  E-Edu Direct Referral Bonus
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#0284C7", fontWeight: 700 }}>
                  Total Transactions: {categoryStats.E_EDU_DIRECT.count} • Total Amount Received: ₹ {fmtAmount(categoryStats.E_EDU_DIRECT.total)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#0369A1", opacity: 0.85, mt: 0.2 }}>
                  Direct sponsor referral bonus (50% net / 75% spendable wallet credit) on direct team upgrades.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {sourceFilter === "E_EDU" && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#EFF6FF", border: "1px solid #BFDBFE" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#DBEAFE", color: "#1D4ED8", width: 38, height: 38 }}>
                <SchoolRoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#1E3A8A" }}>
                  E-Edu Agent Commissions
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#1D4ED8", fontWeight: 700 }}>
                  Total Transactions: {categoryStats.E_EDU.count} • Total Amount Received: ₹ {fmtAmount(categoryStats.E_EDU.total)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#1E40AF", opacity: 0.85, mt: 0.2 }}>
                  Commissions earned from digital courses, LMS modules, and certification on asiyapp.com.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {(sourceFilter === "SAVE" || sourceFilter === "SELF_ACCOUNT") && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#FFFBEB", border: "1px solid #FDE68A" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#FEF3C7", color: "#B45309", width: 38, height: 38 }}>
                <SavingsIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#92400E" }}>
                  Self Account / Self Block Repurchase Pocket
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#B45309", fontWeight: 700 }}>
                  Total Transactions: {categoryStats.SAVE.count} • Total Amount Saved: ₹ {fmtAmount(categoryStats.SAVE.total)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#92400E", opacity: 0.85, mt: 0.2 }}>
                  Automatic 25% allocation reserved for the ₹250 Self-Rebirth loop and shopping vouchers.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {sourceFilter === "SSV_LAYER" && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#F5F3FF", border: "1px solid #DDD6FE" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#EDE9FE", color: "#7C3AED", width: 38, height: 38 }}>
                <LayersRoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#5B21B6" }}>
                  SSV Layer Commissions
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#7C3AED", fontWeight: 700 }}>
                  Total Transactions: {categoryStats.SSV_LAYER.count} • Total Amount Received: ₹ {fmtAmount(categoryStats.SSV_LAYER.total)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#6D28D9", opacity: 0.85, mt: 0.2 }}>
                  Unilevel matrix layer commissions distributed from SSV (Smart Subscription Voucher) purchases.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {sourceFilter === "SSV_BONUS" && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#FFF7ED", border: "1px solid #FFEDD5" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#FFEDD5", color: "#EA580C", width: 38, height: 38 }}>
                <StarsRoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#9A3412" }}>
                  SSV Direct Referral Bonus
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#EA580C", fontWeight: 700 }}>
                  Total Transactions: {categoryStats.SSV_BONUS.count} • Total Amount Received: ₹ {fmtAmount(categoryStats.SSV_BONUS.total)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#C2410C", opacity: 0.85, mt: 0.2 }}>
                  Direct sponsorship bonus earned from SSV (Smart Subscription Voucher) direct package activations.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {sourceFilter === "QR_SCANNER" && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#ECFDF5", border: "1px solid #A7F3D0" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#D1FAE5", color: "#065F46", width: 38, height: 38 }}>
                <QrCode2RoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#065F46" }}>
                  QR Scanner & Merchant Overrides
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#047857", fontWeight: 700 }}>
                  Total Transactions: {categoryStats.QR_SCANNER.count} • Total Amount Received: ₹ {fmtAmount(categoryStats.QR_SCANNER.total)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#065F46", opacity: 0.85, mt: 0.2 }}>
                  Instant payouts from local merchant QR standee transactions and POS scans.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {sourceFilter === "TRIZONE" && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#F0F9FF", border: "1px solid #BAE6FD" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#E0F2FE", color: "#0369A1", width: 38, height: 38 }}>
                <ShoppingCartRoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#0C4A6E" }}>
                  TriZone Shopping & Retail Cashback
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#0284C7", fontWeight: 700 }}>
                  Total Transactions: {categoryStats.TRIZONE.count} • Total Amount Received: ₹ {fmtAmount(categoryStats.TRIZONE.total)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#0369A1", opacity: 0.85, mt: 0.2 }}>
                  Earnings from 18 TriZone commerce categories across your regional network.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {sourceFilter === "ROYALTY" && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#FAF5FF", border: "1px solid #E9D5FF" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#F3E8FF", color: "#7E22CE", width: 38, height: 38 }}>
                <WorkspacePremiumRoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#581C87" }}>
                  Territory & Midnight Royalty
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#7E22CE", fontWeight: 700 }}>
                  Total Transactions: {categoryStats.ROYALTY.count} • Total Amount Received: ₹ {fmtAmount(categoryStats.ROYALTY.total)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#6B21A8", opacity: 0.85, mt: 0.2 }}>
                  Daily midnight distributions from Pincode, District, and State pool turnover.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {sourceFilter === "WITHDRAWAL" && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#FEF2F2", border: "1px solid #FECACA" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#FEE2E2", color: "#DC2626", width: 38, height: 38 }}>
                <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#991B1B" }}>
                  Bank Withdrawals & Transfers
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#DC2626", fontWeight: 700 }}>
                  Total Transactions: {filteredStats?.count ?? categoryStats.WITHDRAWAL?.count ?? 0} • Total Amount Debited: ₹ {fmtAmount(filteredStats?.total ?? categoryStats.WITHDRAWAL?.total ?? 0)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#B91C1C", opacity: 0.85, mt: 0.2 }}>
                  Direct bank withdrawals, pending requests, and wallet-to-wallet transfers.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {termFilter !== "ALL" && dbTermStats[termFilter] && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#F0FDF4", border: "1px solid #A7F3D0" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#DCFCE7", color: "#065F46", width: 38, height: 38 }}>
                <SchoolRoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#064E3B" }}>
                  Term {termFilter} Earning Progress
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#065F46", fontWeight: 800 }}>
                  Active Capped Bucket: ₹ {fmtAmount(dbTermStats[termFilter].cycle_earned)} / ₹ {fmtAmount(dbTermStats[termFilter].cycle_cap || 150000)} ({(Number(dbTermStats[termFilter].cycle_earned || 0) / Number(dbTermStats[termFilter].cycle_cap || 150000) * 100).toFixed(1)}%)
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#047857", mt: 0.2 }}>
                  Total Cash Credited: ₹ {fmtAmount(dbTermStats[termFilter].total)} • Matrix Layers: ₹ {fmtAmount(dbTermStats[termFilter].layer_total)} • Direct Bonuses: ₹ {fmtAmount(dbTermStats[termFilter].direct_total)}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {sourceFilter === "ALL" && (
          <Paper elevation={0} sx={{ p: 1.6, borderRadius: "16px", bgcolor: "#F0FDF4", border: "1px solid #BBF7D0" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#DCFCE7", color: "#166534", width: 38, height: 38 }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#166534" }}>
                  All Transactions Ledger
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#15803D", fontWeight: 700 }}>
                  Total Transactions: {categoryStats.ALL.count} • Total Amount Received: ₹ {fmtAmount(categoryStats.ALL.total)}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#166534", opacity: 0.85, mt: 0.2 }}>
                  Live synchronized statement showing 75% Main Wallet and 25% Self Block payouts.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {/* Compact Segmented Tabs: All (X), Credits (X), Debits (X) */}
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          <Button
            size="small"
            onClick={() => setFlowFilter("ALL")}
            sx={{
              flex: 1,
              py: 0.7,
              borderRadius: "20px",
              fontSize: 12,
              fontWeight: 800,
              textTransform: "none",
              bgcolor: flowFilter === "ALL" ? "#0F172A" : "#FFFFFF",
              color: flowFilter === "ALL" ? "#FFFFFF" : "#64748B",
              border: "1px solid",
              borderColor: flowFilter === "ALL" ? "#0F172A" : "#E2E8F0",
              "&:hover": { bgcolor: flowFilter === "ALL" ? "#1E293B" : "#F8FAFC" },
            }}
          >
            All ({filteredStats?.count != null && (sourceFilter !== "ALL" || termFilter !== "ALL" || flowFilter !== "ALL" || searchQuery) ? filteredStats.count : (categoryStats.ALL.count || allTransactions.length)})
          </Button>

          <Button
            size="small"
            onClick={() => setFlowFilter("CREDIT")}
            sx={{
              flex: 1,
              py: 0.7,
              borderRadius: "20px",
              fontSize: 12,
              fontWeight: 800,
              textTransform: "none",
              bgcolor: flowFilter === "CREDIT" ? "#059669" : "#FFFFFF",
              color: flowFilter === "CREDIT" ? "#FFFFFF" : "#64748B",
              border: "1px solid",
              borderColor: flowFilter === "CREDIT" ? "#059669" : "#E2E8F0",
              "&:hover": { bgcolor: flowFilter === "CREDIT" ? "#047857" : "#F8FAFC" },
            }}
          >
            Credits ({flowFilter === "CREDIT" && filteredStats?.count != null ? filteredStats.count : ledgerSummary.creditCount})
          </Button>

          <Button
            size="small"
            onClick={() => setFlowFilter("DEBIT")}
            sx={{
              flex: 1,
              py: 0.7,
              borderRadius: "20px",
              fontSize: 12,
              fontWeight: 800,
              textTransform: "none",
              bgcolor: flowFilter === "DEBIT" ? "#7C3AED" : "#FFFFFF",
              color: flowFilter === "DEBIT" ? "#FFFFFF" : "#64748B",
              border: "1px solid",
              borderColor: flowFilter === "DEBIT" ? "#7C3AED" : "#E2E8F0",
              "&:hover": { bgcolor: flowFilter === "DEBIT" ? "#6D28D9" : "#F8FAFC" },
            }}
          >
            Debits ({flowFilter === "DEBIT" && filteredStats?.count != null ? filteredStats.count : (ledgerSummary.debitCount || ledgerSummary.selfCount)})
          </Button>
        </Stack>
      </Stack>

      {/* 4. MONTHLY TRANSACTION GROUPING STREAM */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "20px",
          border: "1px solid #EEF2F6",
          bgcolor: "#FFFFFF",
          p: { xs: 1.5, sm: 2 },
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.2, px: 0.5 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 900, color: "#0F172A" }}>
            Transactions ({filteredStats?.count != null ? filteredStats.count : filteredTransactions.length})
            {activeFilterCount > 0 && filteredStats?.total ? ` • ₹${fmtAmount(filteredStats.total)}` : ""}
          </Typography>
          {activeFilterCount > 0 && (
            <Chip
              size="small"
              label="Reset Filters"
              onClick={handleResetFilters}
              sx={{ height: 20, fontSize: 10.5, fontWeight: 700, cursor: "pointer", bgcolor: "#F1F5F9" }}
            />
          )}
        </Box>

        {loading ? (
          <LinearProgress />
        ) : err ? (
          <Typography variant="body2" color="error">
            {err}
          </Typography>
        ) : (
          <SectionList
            sections={sections}
            fallbackRows={filteredTransactions}
            onRowClick={(tx) => {
              setSelectedTx(tx);
              setTxDetailOpen(true);
            }}
          />
        )}
      </Paper>

      {/* PhonePe Filter Bottom Sheet */}
      <PhonePeFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        termFilter={termFilter}
        setTermFilter={setTermFilter}
        purchasedTerms={purchasedTerms}
        datePreset={datePreset}
        setDatePreset={setDatePreset}
        customStartDate={customStartDate}
        setCustomStartDate={setCustomStartDate}
        customEndDate={customEndDate}
        setCustomEndDate={setCustomEndDate}
        flowFilter={flowFilter}
        setFlowFilter={setFlowFilter}
        sourceFilter={sourceFilter}
        setSourceFilter={setSourceFilter}
        onReset={handleResetFilters}
      />

      {/* Transaction Details Bottom Sheet */}
      <TxDetailDrawer
        open={txDetailOpen}
        onClose={() => setTxDetailOpen(false)}
        tx={selectedTx}
      />

      </Box>
    </Box>
  );
}
